import type { CalcAst, LodExpr } from './ast'
import { parseTableauCalc } from './tableauParser'
import { typecheckCalcAst } from './typecheck'
import { resolveFieldOrParamOrSet, type ResolveContext } from './resolver'
import { renderOverClause } from './tableCalcs'
import { getCompleteCalcOrder } from './tableCalcOrder'
import { cleanBracketedFieldRef, normalizeTableauDatasourceKey, normalizeTableauFieldKey, normalizeTableauFieldToken } from '../../tableau/fieldIdentity'

function pad2(n: number): string {
  return String(Math.trunc(n)).padStart(2, '0')
}

function buildLocalDateSql(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = pad2(now.getMonth() + 1)
  const d = pad2(now.getDate())
  return `DATE '${y}-${m}-${d}'`
}

function buildLocalTimestampSql(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = pad2(now.getMonth() + 1)
  const d = pad2(now.getDate())
  const hh = pad2(now.getHours())
  const mm = pad2(now.getMinutes())
  const ss = pad2(now.getSeconds())
  return `TIMESTAMP '${y}-${m}-${d} ${hh}:${mm}:${ss}'`
}

function partitionOnlyOverClause(overClauseRaw: string): string {
  const s = String(overClauseRaw || '').trim()
  if (!s) return ''
  const upper = s.toUpperCase()
  const idxOrder = upper.indexOf('ORDER BY')
  const idxRows = upper.indexOf('ROWS')
  const idxRange = upper.indexOf('RANGE')
  const cuts = [idxOrder, idxRows, idxRange].filter((i) => i >= 0)
  const end = cuts.length ? Math.min(...cuts) : s.length
  return s.slice(0, end).trim()
}

function singleOrderExpr(overClauseRaw: string): { expr: string; direction: 'ASC' | 'DESC' } | null {
  const s = String(overClauseRaw || '').trim()
  const upper = s.toUpperCase()
  const idxOrder = upper.indexOf('ORDER BY')
  if (idxOrder < 0) return null
  const tail = s.slice(idxOrder + 'ORDER BY'.length)
  const tailUpper = tail.toUpperCase()
  const idxRows = tailUpper.indexOf(' ROWS ')
  const idxRange = tailUpper.indexOf(' RANGE ')
  const end = [idxRows, idxRange].filter((i) => i >= 0).reduce((a, b) => Math.min(a, b), tail.length)
  let orderExpr = tail.slice(0, end).trim()
  if (!orderExpr) return null
  let depth = 0
  let inSingle = false
  let inDouble = false
  for (let i = 0; i < orderExpr.length; i++) {
    const ch = orderExpr[i]
    const prev = i > 0 ? orderExpr[i - 1] : ''
    if (ch === "'" && !inDouble && prev !== '\\') inSingle = !inSingle
    else if (ch === '"' && !inSingle && prev !== '\\') inDouble = !inDouble
    else if (!inSingle && !inDouble) {
      if (ch === '(') depth++
      else if (ch === ')') depth = Math.max(0, depth - 1)
      else if (ch === ',' && depth === 0) return null
    }
  }
  const dirMatch = orderExpr.match(/\s+(ASC|DESC)(?:\s+NULLS\s+(?:FIRST|LAST))?\s*$/i)
  const direction = dirMatch?.[1]?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC'
  if (dirMatch) orderExpr = orderExpr.slice(0, dirMatch.index).trim()
  orderExpr = orderExpr.replace(/\s+NULLS\s+(?:FIRST|LAST)\s*$/i, '').trim()
  return orderExpr ? { expr: orderExpr, direction } : null
}

function rewriteLinearPreviousValueSequence(duckdbSql: string, overClauseRaw: string): string {
  const src = String(duckdbSql || '')
  if (!src.includes('__TBLCALC__') || !/\bROW_NUMBER\s*\(\)/i.test(src)) return src
  const order = singleOrderExpr(overClauseRaw)
  if (!order) return src
  const partitionOnly = partitionOnlyOverClause(overClauseRaw)
  const overPartition = partitionOnly ? `OVER (${partitionOnly})` : 'OVER ()'
  const orderCast = `TRY_CAST(${order.expr} AS DOUBLE)`
  const anchor = order.direction === 'DESC'
    ? `MAX(${orderCast}) ${overPartition}`
    : `MIN(${orderCast}) ${overPartition}`
  const distance = order.direction === 'DESC'
    ? `(TRY_CAST((${anchor}) AS DOUBLE) - ${orderCast})`
    : `(${orderCast} - TRY_CAST((${anchor}) AS DOUBLE))`
  const pattern = /(?:TRY_CAST\(\s*\(*\s*)+ROW_NUMBER\(\)\s+OVER\s*\(\s*__TBLCALC__\s*\)\s*(?:\)*\s+AS\s+DOUBLE\s*\))+\s*\)*\s*\*\s*TRY_CAST\(\s*'?(?<delta>-?(?:(?:\d+(?:\.\d*)?)|(?:\.\d+))(?:[eE][+-]?\d+)?)'?\s+AS\s+DOUBLE\s*\)/gi
  return src.replace(pattern, (match: string) => {
    const delta = Number(match.match(/TRY_CAST\(\s*'?(-?(?:(?:\d+(?:\.\d*)?)|(?:\.\d+))(?:[eE][+-]?\d+)?)'?\s+AS\s+DOUBLE\s*\)\s*$/i)?.[1] ?? NaN)
    if (!Number.isFinite(delta) || delta === 0) return match
    const deltaSql = String(Math.abs(delta))
    const sequenceIndex = `(ROUND(TRY_CAST(${distance} AS DOUBLE) / NULLIF(TRY_CAST(${deltaSql} AS DOUBLE), 0), 8) + 1)`
    return `${sequenceIndex} * TRY_CAST(${delta} AS DOUBLE)`
  })
}
import type { CalcDependency, CalcScope, CalculatedField, TableCalcConfig, VisualSpec } from '../visualSpec'

export interface CompileError {
  message: string
  start: number
  end: number
}

export interface CompileResult {
  duckdb_sql: string
  dependencies: CalcDependency[]
  result_type: string
  is_aggregate: boolean
  is_table_calc: boolean
  errors: CompileError[]
  // For table calcs, this is the config used/required.
  table_calc?: TableCalcConfig
}

type CompileState = {
  calc_stack: string[]
  calc_cache: Map<string, CompileResult>
  max_calc_depth: number
}

function stripTableauDateLiteral(raw: string): string {
  const s = String(raw ?? '').trim()
  if (s.startsWith('#') && s.endsWith('#') && s.length >= 2) {
    const inner = s.slice(1, -1).trim()
    if (/^\d{4}-\d{2}-\d{2}(?:[ T].+)?$/.test(inner)) return inner
  }
  return s
}

function quoteStringLiteral(v: string): string {
  const cleaned = stripTableauDateLiteral(String(v ?? ''))
  return `'${cleaned.replaceAll("'", "''")}'`
}

function coerceDateAddOperand(sql: string, argAst?: CalcAst): string {
  const s = String(sql ?? '').trim()
  if (!s) return s
  const nullRe = /^\(+\s*NULL\s*\)+$/i
  if (/^NULL$/i.test(s) || nullRe.test(s)) {
    return 'CAST(NULL AS TIMESTAMP)'
  }
  const literalRe = /^'(?:[^']|'')*'$/
  const parenLiteralRe = /^\(+\s*'(?:[^']|'')*'\s*\)+$/
  const isLiteral = literalRe.test(s) || parenLiteralRe.test(s)
  const isStringAst = argAst ? typecheckCalcAst(argAst).result_type === 'string' : false
  if (isLiteral || isStringAst) return `TRY_CAST(${s} AS TIMESTAMP)`

  // Tableau parameter pipelines often surface dates/timestamps as epoch milliseconds (BIGINT).
  // DuckDB does not allow INTEGER + INTERVAL directly, so when a date-ish operand is a
  // numeric epoch, coerce it to a proper TIMESTAMP expression first.
  //
  // We intentionally keep this coercion local to "date operand" contexts (DATEADD, etc.)
  // to avoid changing semantics of plain numeric expressions elsewhere.
  const unwrapParens = (raw: string): string => {
    let out = String(raw ?? '').trim()
    while (out.startsWith('(') && out.endsWith(')')) {
      const inner = out.slice(1, -1).trim()
      if (!inner) break
      out = inner
    }
    return out
  }
  const unwrapped = unwrapParens(s)
  if (/^-?\d+$/.test(unwrapped)) {
    // Heuristic: treat >= 1e12 as epoch milliseconds (covers modern dates like 1770076800000).
    // Smaller integers are ambiguous (IDs, counts, etc.), so we leave them unchanged.
    const n = Number(unwrapped)
    if (Number.isFinite(n) && Math.abs(n) >= 1e12) {
      const v = unwrapped // preserve exact literal string (avoid scientific notation)
      // Important: avoid `(<bigint> * INTERVAL 1 MILLISECOND)` because DuckDB can internally
      // downcast the multiplier to INT32 and overflow for modern epoch-ms values.
      // `to_timestamp()` expects seconds since epoch.
      // DuckDB returns TIMESTAMP WITH TIME ZONE from TO_TIMESTAMP(); cast eagerly to plain TIMESTAMP
      // so interval arithmetic works (`TIMESTAMP + INTERVAL`).
      return `(CAST(TO_TIMESTAMP((CAST(${v} AS DOUBLE) / 1000.0)) AS TIMESTAMP))`
    }
  }
  return s
}

function normalizeKey(s: string): string {
  return String(s ?? '').trim().toLowerCase()
}

function fieldKeyVariants(raw: string): string[] {
  const base = normalizeKey(raw)
  if (!base) return []
  const out = new Set<string>()
  out.add(base)
  const normFieldKey = normalizeTableauFieldKey(raw)
  if (normFieldKey) out.add(normFieldKey)
  return Array.from(out.values()).filter(Boolean)
}

function paramTypeOfFieldRef(ctx: ResolveContext, n: CalcAst | null): 'date' | 'timestamp' | null {
  if (!n || n.kind !== 'field_ref') return null
  const name = String((n as any)?.field ?? '').trim()
  if (!name) return null
  const want = new Set<string>(fieldKeyVariants(name))
  if (want.size === 0) return null
  const params = (ctx?.spec?.data_pane?.parameters || []) as any[]
  const p = params.find((pp) => {
    const candidates = [
      String((pp as any)?.name ?? ''),
      String((pp as any)?.caption ?? ''),
      String((pp as any)?.raw_name ?? ''),
      String((pp as any)?.label ?? ''),
    ].map((x) => x.trim()).filter(Boolean)
    for (const candidate of candidates) {
      for (const variant of fieldKeyVariants(candidate)) {
        if (want.has(variant)) return true
      }
    }
    return false
  }) || null
  if (!p) return null
  const dt = normalizeKey(String((p as any)?.data_type ?? 'unknown'))
  if (dt === 'date') return 'date'
  if (
    dt === 'timestamp' ||
    dt === 'datetime' ||
    dt === 'date_time' ||
    dt === 'timestamptz' ||
    dt === 'timestamp_tz'
  ) return 'timestamp'
  // Heuristic: if the parameter isn't typed but the current value is a large epoch-like
  // integer (milliseconds), treat it as a timestamp for date arithmetic purposes.
  const v = (p as any)?.current_value
  if (typeof v === 'number' && Number.isFinite(v) && Math.abs(v) >= 1e12) return 'timestamp'
  // Additional heuristics: some pipelines surface timestamps as objects (e.g. Arrow Timestamp),
  // or strings like "Timestamp(Microsecond, 1770076800000000)".
  try {
    if (typeof v === 'string') {
      const s = v.trim()
      if (/^Timestamp\(\s*[A-Za-z]+\s*,\s*-?\d+\s*\)$/.test(s)) return 'timestamp'
      if (/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}:\d{2}(\.\d+)?)?/.test(s)) return 'timestamp'
    }
    if (v && typeof v === 'object') {
      const isoFn = (v as any)?.toISOString
      if (typeof isoFn === 'function') return 'timestamp'
      const vv = (v as any)?.valueOf?.()
      if (typeof vv === 'number' && Number.isFinite(vv) && Math.abs(vv) >= 1e12) return 'timestamp'
      const asStr = String(v).trim()
      if (/^Timestamp\(\s*[A-Za-z]+\s*,\s*-?\d+\s*\)$/.test(asStr)) return 'timestamp'
    }
  } catch { }
  return null
}

function compileIfExpr(
  node: Extract<CalcAst, { kind: 'if' }>,
  ctx: ResolveContext,
  st: CompileState,
): { sql: string; deps: CalcDependency[]; errs: CompileError[] } {
  const deps: CalcDependency[] = []
  const errs: CompileError[] = []
  const parts: string[] = []
  const caseBranchCoercion = getCaseBranchCoercion(
    node as unknown as CalcAst,
    node.branches?.[0]?.then ?? null,
    node.else_expr ?? null,
    '',
    '',
  )
  parts.push('CASE')
  for (const b of node.branches) {
    const w = compileAstInner(b.when, ctx, st)
    const t = compileAstInner(b.then, ctx, st)
    deps.push(...w.dependencies, ...t.dependencies)
    errs.push(...w.errors, ...t.errors)
    parts.push(`WHEN ${w.duckdb_sql} THEN ${coerceCaseBranchSql(t.duckdb_sql, caseBranchCoercion)}`)
  }
  if (node.else_expr) {
    const e = compileAstInner(node.else_expr, ctx, st)
    deps.push(...e.dependencies)
    errs.push(...e.errors)
    parts.push(`ELSE ${coerceCaseBranchSql(e.duckdb_sql, caseBranchCoercion)}`)
  }
  parts.push('END')
  return { sql: parts.join(' '), deps, errs }
}

function compileCaseExpr(
  node: Extract<CalcAst, { kind: 'case' }>,
  ctx: ResolveContext,
  st: CompileState,
): { sql: string; deps: CalcDependency[]; errs: CompileError[] } {
  const deps: CalcDependency[] = []
  const errs: CompileError[] = []
  const parts: string[] = []
  const caseBranchCoercion = getCaseBranchCoercion(
    node as unknown as CalcAst,
    node.whens?.[0]?.then ?? null,
    node.else_expr ?? null,
    '',
    '',
  )
  let baseSql: string | null = null
  if (node.base) {
    const b = compileAstInner(node.base, ctx, st)
    deps.push(...b.dependencies)
    errs.push(...b.errors)
    baseSql = b.duckdb_sql
  }
  if (!node.whens || node.whens.length === 0) {
    if (baseSql) return { sql: baseSql, deps, errs }
    if (node.else_expr) {
      const e = compileAstInner(node.else_expr, ctx, st)
      deps.push(...e.dependencies)
      errs.push(...e.errors)
      return { sql: e.duckdb_sql, deps, errs }
    }
    return { sql: 'NULL', deps, errs }
  }
  parts.push('CASE')
  if (baseSql) parts.push(baseSql)
  for (const w of node.whens) {
    const ww = compileAstInner(w.when, ctx, st)
    const tt = compileAstInner(w.then, ctx, st)
    deps.push(...ww.dependencies, ...tt.dependencies)
    errs.push(...ww.errors, ...tt.errors)
    parts.push(`WHEN ${ww.duckdb_sql} THEN ${coerceCaseBranchSql(tt.duckdb_sql, caseBranchCoercion)}`)
  }
  if (node.else_expr) {
    const e = compileAstInner(node.else_expr, ctx, st)
    deps.push(...e.dependencies)
    errs.push(...e.errors)
    parts.push(`ELSE ${coerceCaseBranchSql(e.duckdb_sql, caseBranchCoercion)}`)
  }
  parts.push('END')
  return { sql: parts.join(' '), deps, errs }
}

function isSetSentinel(sql: string): { is: boolean; name: string | null } {
  const m = String(sql || '').match(/^__SET__\('([\s\S]*)'\)$/)
  if (!m) return { is: false, name: null }
  return { is: true, name: m[1] ? m[1].replaceAll("''", "'") : '' }
}

function normalizeSetName(raw: string): string {
  const cleaned = cleanBracketedFieldRef(raw) || raw
  const key = normalizeTableauFieldKey(cleaned)
  const token = normalizeTableauFieldToken(cleaned)
  const base = key || token || cleaned
  return String(base ?? '').trim().toLowerCase().replace(/^:+/, '')
}

const numericAggArg = (sql: string): string => `TRY_CAST(${sql} AS DOUBLE)`
const castVarchar = (sql: string): string => `CAST(${sql} AS VARCHAR)`
const isNumberLiteralAst = (ast?: CalcAst | null): boolean => !!ast && ast.kind === 'literal' && ast.literal_kind === 'number'
const isStringLiteralAst = (ast?: CalcAst | null): boolean => !!ast && ast.kind === 'literal' && ast.literal_kind === 'string'
const looksNumericSql = (sql: string): boolean => {
  const s = String(sql ?? '').trim()
  if (!s) return false
  if (/^[+-]?\d+(?:\.\d+)?$/.test(s)) return true
  if (/\bTRY_CAST\s*\([\s\S]*\bAS\s+DOUBLE\b/i.test(s)) return true
  if (/\bCAST\s*\([\s\S]*\bAS\s+DOUBLE\b/i.test(s)) return true
  if (/\b(?:SUM|AVG|COUNT|COUNTD|ABS|ROUND|CEIL|FLOOR|POWER)\s*\(/i.test(s)) return true
  if (/[+\-*/]/.test(s)) return true
  return false
}
const looksStringSql = (sql: string): boolean => {
  const s = String(sql ?? '').trim()
  if (!s) return false
  if (/^'(?:''|[^'])*'$/.test(s)) return true
  if (/\bCAST\s*\([\s\S]*\bAS\s+VARCHAR\b/i.test(s)) return true
  if (/\bSTRFTIME\s*\(/i.test(s)) return true
  return false
}
type CaseBranchCoercion = 'numeric' | 'string' | 'none'
const getCaseBranchCoercion = (
  nodeAst: CalcAst | null | undefined,
  thenAst: CalcAst | null | undefined,
  elseAst: CalcAst | null | undefined,
  thenSql: string,
  elseSql: string,
): CaseBranchCoercion => {
  try {
    const nodeType = nodeAst ? typecheckCalcAst(nodeAst).result_type : 'unknown'
    if (nodeType === 'string') return 'string'
    if (nodeType === 'number') return 'numeric'
    if (nodeType === 'date' || nodeType === 'timestamp') return 'none'
    const thenType = thenAst ? typecheckCalcAst(thenAst).result_type : 'unknown'
    const elseType = elseAst ? typecheckCalcAst(elseAst).result_type : 'unknown'
    if (thenType === 'string' || elseType === 'string') return 'string'
    if (thenType === 'date' || thenType === 'timestamp' || elseType === 'date' || elseType === 'timestamp') return 'none'
    if (thenType === 'number' || elseType === 'number') return 'numeric'
  } catch { }
  if (looksStringSql(thenSql) || looksStringSql(elseSql)) return 'string'
  if (looksNumericSql(thenSql) || looksNumericSql(elseSql)) return 'numeric'
  return 'none'
}
const coerceCaseBranchSql = (sql: string, coercion: CaseBranchCoercion): string =>
  coercion === 'numeric' ? numericAggArg(sql) : coercion === 'string' ? castVarchar(sql) : sql
const isPreviousValueAst = (ast: CalcAst | null | undefined): ast is Extract<CalcAst, { kind: 'call' }> =>
  !!ast && ast.kind === 'call' && String(ast.name || '').trim().toLowerCase() === 'previous_value'
const coalesceByType = (
  a0Sql: string,
  a1Sql: string,
  a0Ast?: CalcAst | null,
  a1Ast?: CalcAst | null,
): string => {
  const t0 = a0Ast ? typecheckCalcAst(a0Ast).result_type : 'unknown'
  const t1 = a1Ast ? typecheckCalcAst(a1Ast).result_type : 'unknown'
  const arg0NumLit = isNumberLiteralAst(a0Ast)
  const arg1NumLit = isNumberLiteralAst(a1Ast)
  const arg0StrLit = isStringLiteralAst(a0Ast)
  const arg1StrLit = isStringLiteralAst(a1Ast)
  const preferNumeric = t0 === 'number' || t1 === 'number' || arg0NumLit || arg1NumLit
  const preferString = t0 === 'string' || t1 === 'string' || arg0StrLit || arg1StrLit
  if (preferNumeric && !preferString) return `COALESCE(${numericAggArg(a0Sql)}, ${numericAggArg(a1Sql)})`
  if (preferString && !preferNumeric) return `COALESCE(${castVarchar(a0Sql)}, ${castVarchar(a1Sql)})`
  if (arg0NumLit || arg1NumLit) return `COALESCE(${numericAggArg(a0Sql)}, ${numericAggArg(a1Sql)})`
  if (arg0StrLit || arg1StrLit) return `COALESCE(${castVarchar(a0Sql)}, ${castVarchar(a1Sql)})`
  return `COALESCE(${a0Sql}, ${a1Sql})`
}

function findSetDef(ctx: ResolveContext, raw: string): any | null {
  const want = normalizeSetName(raw)
  if (!want) return null
  const sets: any[] = Array.isArray((ctx.spec.data_pane as any)?.sets) ? ((ctx.spec.data_pane as any).sets as any[]) : []
  for (const s of sets) {
    const nm = normalizeSetName(String(s?.name ?? s?.raw_name ?? ''))
    if (nm && nm === want) return s
  }
  return null
}

function compileCall(
  node: Extract<CalcAst, { kind: 'call' }>,
  ctx: ResolveContext,
  st: CompileState,
): { sql: string; deps: CalcDependency[]; errs: CompileError[]; tableCalc?: TableCalcConfig } {
  const deps: CalcDependency[] = []
  const errs: CompileError[] = []
  const name = String(node.name || '').trim().toLowerCase()
  const args = node.args.map((a) => compileAstInner(a, ctx, st))
  for (const a of args) {
    deps.push(...a.dependencies)
    errs.push(...a.errors)
  }
  const a0 = args[0]?.duckdb_sql ?? 'NULL'
  const a1 = args[1]?.duckdb_sql ?? 'NULL'
  const a2 = args[2]?.duckdb_sql ?? 'NULL'
  const unwrapAggregateArgSql = (): string | null => {
    const arg0 = node.args?.[0]
    if (!arg0 || arg0.kind !== 'call') return null
    const nm = String((arg0 as any).name || '').trim().toLowerCase()
    if (!(nm === 'sum' || nm === 'avg' || nm === 'min' || nm === 'max' || nm === 'count' || nm === 'countd')) return null
    const inner = (arg0 as any).args?.[0]
    if (!inner) return null
    return compileAstInner(inner, ctx, st).duckdb_sql
  }
  const a0TableCalc = unwrapAggregateArgSql() ?? a0

  const renderWindowFrame = (start: number | null | undefined, end: number | null | undefined): string => {
    // Tableau window bounds are row-relative offsets:
    // - null/undefined => unbounded
    // - 0 => current row
    // - negative => preceding
    // - positive => following
    const one = (v: number | null | undefined, isStart: boolean): string => {
      if (v === null || v === undefined) return isStart ? 'UNBOUNDED PRECEDING' : 'UNBOUNDED FOLLOWING'
      const n = Math.trunc(Number(v))
      if (!Number.isFinite(n)) return isStart ? 'UNBOUNDED PRECEDING' : 'UNBOUNDED FOLLOWING'
      if (n === 0) return 'CURRENT ROW'
      if (n < 0) return `${Math.abs(n)} PRECEDING`
      return `${n} FOLLOWING`
    }
    return ` ROWS BETWEEN ${one(start, true)} AND ${one(end, false)}`
  }

  const parseWindowBound = (ast: CalcAst | undefined, isStart: boolean): { ok: boolean; value: number | null } => {
    if (!ast) return { ok: true, value: isStart ? null : null }
    if (ast.kind === 'literal' && ast.literal_kind === 'number') {
      const n = Math.trunc(Number(ast.value))
      return Number.isFinite(n) ? { ok: true, value: n } : { ok: false, value: isStart ? null : null }
    }
    if (ast.kind === 'unary' && ast.op === '-' && ast.expr?.kind === 'literal' && ast.expr.literal_kind === 'number') {
      const n = Math.trunc(Number(ast.expr.value))
      return Number.isFinite(n) ? { ok: true, value: -n } : { ok: false, value: isStart ? null : null }
    }
    if (ast.kind === 'call') {
      const nm = String(ast.name || '').trim().toLowerCase()
      // Tableau convention: FIRST()/LAST() used as WINDOW_* bounds mean "full partition".
      if (nm === 'first' && (ast.args?.length ?? 0) === 0) return { ok: true, value: null } // unbounded preceding
      if (nm === 'last' && (ast.args?.length ?? 0) === 0) return { ok: true, value: null } // unbounded following
    }
    return { ok: false, value: isStart ? null : null }
  }

  // Logical helpers
  if (name === 'iif') {
    const caseBranchCoercion = getCaseBranchCoercion(
      node as unknown as CalcAst,
      node.args?.[1] ?? null,
      node.args?.[2] ?? null,
      a1,
      a2,
    )
    const thenSql = coerceCaseBranchSql(a1, caseBranchCoercion)
    const elseSql = coerceCaseBranchSql(a2, caseBranchCoercion)
    return { sql: `CASE WHEN ${a0} THEN ${thenSql} ELSE ${elseSql} END`, deps, errs }
  }
  if (name === 'isnull') {
    return { sql: `(${a0} IS NULL)`, deps, errs }
  }
  if (name === 'ifnull') {
    return { sql: coalesceByType(a0, a1, node.args?.[0], node.args?.[1]), deps, errs }
  }
  if (name === 'zn') {
    return { sql: `COALESCE(${numericAggArg(a0)}, 0.0)`, deps, errs }
  }

  // Type conversions (common Tableau patterns)
  if (name === 'str') {
    return { sql: `CAST(${a0} AS VARCHAR)`, deps, errs }
  }
  if (name === 'int') {
    // Tableau INT() is best-effort; invalid casts yield NULL.
    return { sql: `TRY_CAST(${a0} AS BIGINT)`, deps, errs }
  }
  if (name === 'float') {
    // Tableau FLOAT() is best-effort; invalid casts yield NULL.
    return { sql: `TRY_CAST(${a0} AS DOUBLE)`, deps, errs }
  }
  if (name === 'date') {
    // Tableau DATE() is best-effort; invalid casts yield NULL.
    return { sql: `TRY_CAST(${a0} AS DATE)`, deps, errs }
  }
  if (name === 'datetime') {
    // Tableau DATETIME() is best-effort; invalid casts yield NULL.
    return { sql: `TRY_CAST(${a0} AS TIMESTAMP)`, deps, errs }
  }

  // Numeric functions
  if (name === 'abs') {
    return { sql: `ABS(${a0})`, deps, errs }
  }
  if (name === 'round') {
    // Tableau: ROUND(number, [digits])
    if (node.args?.length >= 2) return { sql: `ROUND(${a0}, ${a1})`, deps, errs }
    return { sql: `ROUND(${a0})`, deps, errs }
  }
  if (name === 'ceiling' || name === 'ceil') {
    return { sql: `CEIL(${a0})`, deps, errs }
  }
  if (name === 'floor') {
    return { sql: `FLOOR(${a0})`, deps, errs }
  }
  if (name === 'power') {
    return { sql: `POWER(${a0}, ${a1})`, deps, errs }
  }
  if (name === 'sqrt') {
    return { sql: `SQRT(${a0})`, deps, errs }
  }

  // String functions
  if (name === 'len' || name === 'length') {
    return { sql: `LENGTH(${a0})`, deps, errs }
  }
  if (name === 'lower') {
    return { sql: `LOWER(${a0})`, deps, errs }
  }
  if (name === 'upper') {
    return { sql: `UPPER(${a0})`, deps, errs }
  }
  if (name === 'trim') {
    return { sql: `TRIM(${a0})`, deps, errs }
  }
  if (name === 'ltrim') {
    return { sql: `LTRIM(${a0})`, deps, errs }
  }
  if (name === 'rtrim') {
    return { sql: `RTRIM(${a0})`, deps, errs }
  }
  if (name === 'left') {
    // Tableau: LEFT(string, n)
    return { sql: `SUBSTR(${a0}, 1, TRY_CAST(${a1} AS BIGINT))`, deps, errs }
  }
  if (name === 'right') {
    // Tableau: RIGHT(string, n)
    // DuckDB SUBSTR supports negative start; use a portable expression.
    const len = `TRY_CAST(${a1} AS BIGINT)`
    return { sql: `SUBSTR(${a0}, (LENGTH(${a0}) - ${len} + 1), ${len})`, deps, errs }
  }
  if (name === 'mid') {
    // Tableau: MID(string, start, length)
    return { sql: `SUBSTR(${a0}, TRY_CAST(${a1} AS BIGINT), TRY_CAST(${a2} AS BIGINT))`, deps, errs }
  }
  if (name === 'replace') {
    return { sql: `REPLACE(${a0}, ${a1}, ${a2})`, deps, errs }
  }
  if (name === 'char') {
    // Tableau: CHAR(int) -> single character
    // DuckDB: CHR(int)
    return { sql: `CHR(TRY_CAST(${a0} AS INTEGER))`, deps, errs }
  }
  if (name === 'ascii') {
    // Tableau: ASCII(string) -> integer code for first character (best-effort).
    if ((node.args?.length ?? 0) < 1) {
      errs.push({ message: 'ASCII(string) requires 1 argument', start: node.span.start, end: node.span.end })
      return { sql: 'NULL', deps, errs }
    }
    // DuckDB supports ASCII(); if not, this can be replaced with a UNICODE()/SUBSTR fallback.
    return { sql: `ASCII(${a0})`, deps, errs }
  }
  if (name === 'find') {
    // Tableau: FIND(string, substring, [start])
    // Returns 1-based position; 0 if not found.
    const s0 = `TRY_CAST(${a0} AS VARCHAR)`
    const s1 = `TRY_CAST(${a1} AS VARCHAR)`
    if ((node.args?.length ?? 0) < 2) {
      errs.push({ message: 'FIND(string, substring, [start]) requires at least 2 arguments', start: node.span.start, end: node.span.end })
      return { sql: '0', deps, errs }
    }
    if ((node.args?.length ?? 0) < 3) {
      return { sql: `POSITION(${s1} IN ${s0})`, deps, errs }
    }
    const startRaw = `TRY_CAST(${a2} AS INTEGER)`
    const start = `COALESCE(${startRaw}, 1)`
    const sub = `SUBSTR(${s0}, ${start})`
    const pos = `POSITION(${s1} IN ${sub})`
    return { sql: `(CASE WHEN ${start} <= 1 THEN POSITION(${s1} IN ${s0}) WHEN ${pos} = 0 THEN 0 ELSE (${pos} + ${start} - 1) END)`, deps, errs }
  }
  if (name === 'findnth') {
    // Tableau: FINDNTH(string, substring, nth)
    // Best-effort: support only a small literal nth (to avoid enormous SQL).
    const s0 = `TRY_CAST(${a0} AS VARCHAR)`
    const s1 = `TRY_CAST(${a1} AS VARCHAR)`
    if ((node.args?.length ?? 0) < 3) {
      errs.push({ message: 'FINDNTH(string, substring, nth) requires 3 arguments', start: node.span.start, end: node.span.end })
      return { sql: '0', deps, errs }
    }
    const nth = (() => {
      const a = node.args?.[2]
      if (!a) return null
      if (a.kind === 'literal' && (a as any).literal_kind === 'number') {
        const n = Math.trunc(Number((a as any).value))
        return Number.isFinite(n) ? n : null
      }
      if (a.kind === 'unary' && (a as any).op === '+' && (a as any).expr?.kind === 'literal' && (a as any).expr.literal_kind === 'number') {
        const n = Math.trunc(Number((a as any).expr.value))
        return Number.isFinite(n) ? n : null
      }
      return null
    })()
    if (nth === null) {
      errs.push({ message: 'FINDNTH currently requires a literal integer nth (e.g. 2)', start: node.span.start, end: node.span.end })
      return { sql: '0', deps, errs }
    }
    if (nth <= 0) return { sql: '0', deps, errs }
    if (nth > 20) {
      errs.push({ message: 'FINDNTH nth is too large (max 20 in v0)', start: node.span.start, end: node.span.end })
      return { sql: '0', deps, errs }
    }

    // First occurrence:
    let p = `POSITION(${s1} IN ${s0})`
    if (nth === 1) return { sql: p, deps, errs }

    // Advance by substring length each time.
    for (let i = 2; i <= nth; i++) {
      const rest = `SUBSTR(${s0}, (${p} + LENGTH(${s1})))`
      const next = `POSITION(${s1} IN ${rest})`
      p = `(CASE
        WHEN (${p}) = 0 THEN 0
        WHEN (${next}) = 0 THEN 0
        ELSE ((${p}) + LENGTH(${s1}) - 1 + (${next}))
      END)`.replace(/\s+/g, ' ').trim()
    }
    return { sql: p, deps, errs }
  }
  if (name === 'contains') {
    // Tableau: CONTAINS(string, substring) -> bool
    const s0 = `TRY_CAST(${a0} AS VARCHAR)`
    const s1 = `TRY_CAST(${a1} AS VARCHAR)`
    return { sql: `(POSITION(${s1} IN ${s0}) > 0)`, deps, errs }
  }
  if (name === 'startswith') {
    const s0 = `TRY_CAST(${a0} AS VARCHAR)`
    const s1 = `TRY_CAST(${a1} AS VARCHAR)`
    return { sql: `STARTS_WITH(${s0}, ${s1})`, deps, errs }
  }
  if (name === 'endswith') {
    const s0 = `TRY_CAST(${a0} AS VARCHAR)`
    const s1 = `TRY_CAST(${a1} AS VARCHAR)`
    return { sql: `ENDS_WITH(${s0}, ${s1})`, deps, errs }
  }

  // Date/time
  if (name === 'today') {
    return { sql: buildLocalDateSql(), deps, errs }
  }
  if (name === 'now') {
    return { sql: buildLocalTimestampSql(), deps, errs }
  }
  if (name === 'makedate') {
    if ((node.args?.length ?? 0) < 3) {
      errs.push({ message: 'MAKEDATE(year, month, day) requires 3 arguments', start: node.span.start, end: node.span.end })
      return { sql: 'NULL', deps, errs }
    }
    return { sql: `MAKE_DATE(${a0}, ${a1}, ${a2})`, deps, errs }
  }
  if (name === 'dateadd') {
    // Tableau: DATEADD(date_part, interval, date)
    if ((node.args?.length ?? 0) < 3) {
      errs.push({ message: 'DATEADD(date_part, interval, date) requires 3 arguments', start: node.span.start, end: node.span.end })
      return { sql: 'NULL', deps, errs }
    }
    const partRaw = (node.args?.[0]?.kind === 'literal' && (node.args[0] as any).literal_kind === 'string')
      ? String((node.args[0] as any).value || '').trim().toLowerCase()
      : null
    const nSql = a1
    const dSql = coerceDateAddOperand(a2, node.args?.[2])
    const mul = (k: number) => (k === 1 ? `(${nSql})` : `((${nSql}) * ${k})`)
    if (!partRaw) {
      errs.push({ message: 'DATEADD requires a literal date_part (e.g. "day")', start: node.span.start, end: node.span.end })
      return { sql: `(${dSql} + (${nSql}) * INTERVAL 1 DAY)`, deps, errs }
    }
    const part = partRaw.replace(/'/g, '').trim()
    if (part === 'year' || part === 'yy' || part === 'yyyy') return { sql: `(${dSql} + ${mul(1)} * INTERVAL 1 YEAR)`, deps, errs }
    if (part === 'quarter' || part === 'qq') return { sql: `(${dSql} + ${mul(3)} * INTERVAL 1 MONTH)`, deps, errs }
    if (part === 'month' || part === 'mm') return { sql: `(${dSql} + ${mul(1)} * INTERVAL 1 MONTH)`, deps, errs }
    if (part === 'week' || part === 'wk') return { sql: `(${dSql} + ${mul(7)} * INTERVAL 1 DAY)`, deps, errs }
    if (part === 'day' || part === 'dd') return { sql: `(${dSql} + ${mul(1)} * INTERVAL 1 DAY)`, deps, errs }
    if (part === 'hour' || part === 'hh') return { sql: `(${dSql} + ${mul(1)} * INTERVAL 1 HOUR)`, deps, errs }
    if (part === 'minute' || part === 'mi') return { sql: `(${dSql} + ${mul(1)} * INTERVAL 1 MINUTE)`, deps, errs }
    if (part === 'second' || part === 'ss') return { sql: `(${dSql} + ${mul(1)} * INTERVAL 1 SECOND)`, deps, errs }
    errs.push({ message: `Unsupported DATEADD part "${partRaw}"`, start: node.span.start, end: node.span.end })
    return { sql: `(${dSql} + (${nSql}) * INTERVAL 1 DAY)`, deps, errs }
  }
  if (name === 'datediff') {
    // Tableau: DATEDIFF(date_part, start, end)
    if ((node.args?.length ?? 0) < 3) {
      errs.push({ message: 'DATEDIFF(date_part, start, end) requires 3 arguments', start: node.span.start, end: node.span.end })
      return { sql: 'NULL', deps, errs }
    }
    const partRaw = (node.args?.[0]?.kind === 'literal' && (node.args[0] as any).literal_kind === 'string')
      ? String((node.args[0] as any).value || '').trim().toLowerCase()
      : null
    const startSql = `TRY_CAST(${coerceDateAddOperand(a1, node.args?.[1])} AS TIMESTAMP)`
    const endSql = `TRY_CAST(${coerceDateAddOperand(a2, node.args?.[2])} AS TIMESTAMP)`
    if (!partRaw) {
      errs.push({ message: 'DATEDIFF requires a literal date_part (e.g. "day")', start: node.span.start, end: node.span.end })
      return { sql: `DATE_DIFF('day', ${startSql}, ${endSql})`, deps, errs }
    }
    const part = partRaw.replace(/'/g, '').trim()
    if (part === 'quarter' || part === 'qq') {
      return { sql: `(DATE_DIFF('month', ${startSql}, ${endSql}) / 3)`, deps, errs }
    }
    const duckPart =
      (part === 'year' || part === 'yy' || part === 'yyyy') ? 'year'
      : (part === 'month' || part === 'mm') ? 'month'
      : (part === 'week' || part === 'wk') ? 'week'
      : (part === 'day' || part === 'dd') ? 'day'
      : (part === 'hour' || part === 'hh') ? 'hour'
      : (part === 'minute' || part === 'mi') ? 'minute'
      : (part === 'second' || part === 'ss') ? 'second'
      : null
    if (!duckPart) {
      errs.push({ message: `Unsupported DATEDIFF part "${partRaw}"`, start: node.span.start, end: node.span.end })
      return { sql: `DATE_DIFF('day', ${startSql}, ${endSql})`, deps, errs }
    }
    return { sql: `DATE_DIFF('${duckPart}', ${startSql}, ${endSql})`, deps, errs }
  }
  if (name === 'datepart') {
    // Tableau: DATEPART(date_part, date)
    if ((node.args?.length ?? 0) < 2) {
      errs.push({ message: 'DATEPART(date_part, date) requires 2 arguments', start: node.span.start, end: node.span.end })
      return { sql: 'NULL', deps, errs }
    }
    const partRaw = (node.args?.[0]?.kind === 'literal' && (node.args[0] as any).literal_kind === 'string')
      ? String((node.args[0] as any).value || '').trim().toLowerCase()
      : null
    const dSql = a1
    if (!partRaw) {
      errs.push({ message: 'DATEPART requires a literal date_part (e.g. "year")', start: node.span.start, end: node.span.end })
      return { sql: `DATE_PART('year', ${dSql})`, deps, errs }
    }
    const part = partRaw.replace(/'/g, '').trim()
    if (part === 'weekday' || part === 'dayofweek' || part === 'dw') {
      return { sql: `(CAST(DATE_PART('dow', ${dSql}) AS INTEGER) + 1)`, deps, errs }
    }
    if (part === 'quarter' || part === 'qq') return { sql: `DATE_PART('quarter', ${dSql})`, deps, errs }
    const duckPart =
      (part === 'year' || part === 'yy' || part === 'yyyy') ? 'year'
      : (part === 'month' || part === 'mm') ? 'month'
      : (part === 'week' || part === 'wk') ? 'week'
      : (part === 'day' || part === 'dd') ? 'day'
      : (part === 'hour' || part === 'hh') ? 'hour'
      : (part === 'minute' || part === 'mi') ? 'minute'
      : (part === 'second' || part === 'ss') ? 'second'
      : null
    if (!duckPart) {
      errs.push({ message: `Unsupported DATEPART part "${partRaw}"`, start: node.span.start, end: node.span.end })
      return { sql: `DATE_PART('year', ${dSql})`, deps, errs }
    }
    return { sql: `DATE_PART('${duckPart}', ${dSql})`, deps, errs }
  }
  if (name === 'year' || name === 'quarter' || name === 'month' || name === 'week' || name === 'day' || name === 'hour' || name === 'minute' || name === 'second' || name === 'weekday') {
    // Tableau date part convenience functions: YEAR(date), MONTH(date), ...
    if ((node.args?.length ?? 0) < 1) {
      errs.push({ message: `${name.toUpperCase()}(date) requires 1 argument`, start: node.span.start, end: node.span.end })
      return { sql: 'NULL', deps, errs }
    }
    const dSql = a0
    if (name === 'weekday') {
      // Tableau WEEKDAY: 1=Sunday ... 7=Saturday (best-effort).
      return { sql: `(CAST(DATE_PART('dow', ${dSql}) AS INTEGER) + 1)`, deps, errs }
    }
    const part =
      (name === 'year') ? 'year'
      : (name === 'quarter') ? 'quarter'
      : (name === 'month') ? 'month'
      : (name === 'week') ? 'week'
      : (name === 'day') ? 'day'
      : (name === 'hour') ? 'hour'
      : (name === 'minute') ? 'minute'
      : (name === 'second') ? 'second'
      : 'year'
    return { sql: `CAST(DATE_PART('${part}', ${dSql}) AS INTEGER)`, deps, errs }
  }
  if (name === 'datename') {
    // Tableau: DATENAME(date_part, date) -> string label
    if ((node.args?.length ?? 0) < 2) {
      errs.push({ message: 'DATENAME(date_part, date) requires 2 arguments', start: node.span.start, end: node.span.end })
      return { sql: 'NULL', deps, errs }
    }
    const partRaw = (node.args?.[0]?.kind === 'literal' && (node.args[0] as any).literal_kind === 'string')
      ? String((node.args[0] as any).value || '').trim().toLowerCase()
      : null
    const dSql = a1
    const ts = `TRY_CAST(${dSql} AS TIMESTAMP)`
    if (!partRaw) {
      errs.push({ message: 'DATENAME requires a literal date_part (e.g. "month")', start: node.span.start, end: node.span.end })
      return { sql: `CAST(DATE_PART('year', ${ts}) AS VARCHAR)`, deps, errs }
    }
    const part = partRaw.replace(/'/g, '').trim()
    if (part === 'month' || part === 'mm') return { sql: `STRFTIME(${ts}, '%B')`, deps, errs }
    if (part === 'weekday' || part === 'dayofweek' || part === 'dw') return { sql: `STRFTIME(${ts}, '%A')`, deps, errs }
    if (part === 'quarter' || part === 'qq') return { sql: `CAST(DATE_PART('quarter', ${ts}) AS VARCHAR)`, deps, errs }
    const duckPart =
      (part === 'year' || part === 'yy' || part === 'yyyy') ? 'year'
      : (part === 'day' || part === 'dd') ? 'day'
      : (part === 'hour' || part === 'hh') ? 'hour'
      : (part === 'minute' || part === 'mi') ? 'minute'
      : (part === 'second' || part === 'ss') ? 'second'
      : null
    if (!duckPart) {
      errs.push({ message: `Unsupported DATENAME part "${partRaw}"`, start: node.span.start, end: node.span.end })
      return { sql: `CAST(DATE_PART('year', ${ts}) AS VARCHAR)`, deps, errs }
    }
    return { sql: `CAST(DATE_PART('${duckPart}', ${ts}) AS VARCHAR)`, deps, errs }
  }
  if (name === 'datetrunc') {
    // Tableau: DATETRUNC(date_part, date)
    if ((node.args?.length ?? 0) < 2) {
      errs.push({ message: 'DATETRUNC(date_part, date) requires 2 arguments', start: node.span.start, end: node.span.end })
      return { sql: 'NULL', deps, errs }
    }
    const partRaw = (node.args?.[0]?.kind === 'literal' && (node.args[0] as any).literal_kind === 'string')
      ? String((node.args[0] as any).value || '').trim().toLowerCase()
      : null
    const dSql = a1
    const ts = `TRY_CAST(${dSql} AS TIMESTAMP)`
    if (!partRaw) {
      errs.push({ message: 'DATETRUNC requires a literal date_part (e.g. "month")', start: node.span.start, end: node.span.end })
      return { sql: `DATE_TRUNC('day', ${ts})`, deps, errs }
    }
    const part = partRaw.replace(/'/g, '').trim()
    const duckPart =
      (part === 'year' || part === 'yy' || part === 'yyyy') ? 'year'
      : (part === 'quarter' || part === 'qq') ? 'quarter'
      : (part === 'month' || part === 'mm') ? 'month'
      : (part === 'week' || part === 'wk') ? 'week'
      : (part === 'day' || part === 'dd') ? 'day'
      : (part === 'hour' || part === 'hh') ? 'hour'
      : (part === 'minute' || part === 'mi') ? 'minute'
      : (part === 'second' || part === 'ss') ? 'second'
      : null
    if (!duckPart) {
      errs.push({ message: `Unsupported DATETRUNC part "${partRaw}"`, start: node.span.start, end: node.span.end })
      return { sql: `DATE_TRUNC('day', ${ts})`, deps, errs }
    }
    return { sql: `DATE_TRUNC('${duckPart}', ${ts})`, deps, errs }
  }

  // Aggregates
  if (name === 'agg') {
    // Tableau AGG(expr): argument is already aggregated.
    return { sql: `${a0}`, deps, errs }
  }
  if (name === 'attr') {
    // Tableau ATTR: value if unique else "*".
    // If all values are NULL, return NULL (Tableau-like).
    const v = `CAST(${a0} AS VARCHAR)`
    const c = `COUNT(DISTINCT ${v})`
    return { sql: `CASE WHEN ${c} = 0 THEN NULL WHEN ${c} = 1 THEN MIN(${v}) ELSE '*' END`, deps, errs }
  }
  if (name === 'countd') {
    if (node.args?.[0]?.kind === 'lod') {
      // Preserve the outer aggregate intent for downstream grouped-query LOD rewrites.
      return { sql: `/* LOD_OUTER_AGG COUNTD */ ${a0}`, deps, errs }
    }
    return { sql: `COUNT(DISTINCT ${a0})`, deps, errs }
  }
  // Tableau frequently wraps LOD results in an outer aggregate (e.g. SUM({FIXED ...})).
  // We still avoid nested aggregate SQL here, but we tag the dropped aggregate so the
  // grouped-query LOD rewrite can apply the intended outer aggregation semantics.
  if ((name === 'count' || name === 'sum' || name === 'avg' || name === 'min' || name === 'max') && node.args?.[0]?.kind === 'lod') {
    return { sql: `/* LOD_OUTER_AGG ${name.toUpperCase()} */ ${a0}`, deps, errs }
  }
  if (name === 'sum' || name === 'avg') {
    return { sql: `${name.toUpperCase()}(${numericAggArg(a0)})`, deps, errs }
  }
  if (name === 'count' || name === 'min' || name === 'max') {
    return { sql: `${name.toUpperCase()}(${a0})`, deps, errs }
  }

  // Table calcs: we compile to window funcs, but the final OVER(...) is injected later once compute-using is known.
  // For now, we emit a placeholder OVER() and attach a default config if missing.
  if (name === 'rank' || name === 'rank_dense') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const dirRaw = (node.args?.[1]?.kind === 'literal' && (node.args[1] as any).literal_kind === 'string')
      ? String((node.args[1] as any).value || '').trim().toLowerCase()
      : null
    const dir = (dirRaw === 'asc' || dirRaw === 'ascending') ? 'ASC' : (dirRaw === 'desc' || dirRaw === 'descending') ? 'DESC' : 'DESC'
    const fn = (name === 'rank_dense') ? 'DENSE_RANK' : 'RANK'
    // IMPORTANT: rank order is based on the expression value; partitioning comes from compute-using.
    // We inject only the PARTITION BY clause later via a dedicated placeholder.
    return { sql: `${fn}() OVER (__TBLCALC_PARTITION__ ORDER BY ${a0} ${dir})`, deps, errs, tableCalc: cfg }
  }
  const numericTableCalcArg = numericAggArg(a0TableCalc)
  if (name === 'running_sum') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    // Tableau: running sum over addressing order.
    const frame = renderWindowFrame(null, 0)
    return { sql: `SUM(${numericTableCalcArg}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'window_sum') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    // Tableau WINDOW_* defaults to the full partition when bounds are omitted.
    const start = parseWindowBound(node.args?.[1], true)
    const end = parseWindowBound(node.args?.[2], false)
    if (!start.ok || !end.ok) {
      errs.push({ message: 'Unsupported WINDOW_SUM bounds; defaulting to full partition', start: node.span.start, end: node.span.end })
    }
    const frame = renderWindowFrame(start.ok ? start.value : null, end.ok ? end.value : null)
    return { sql: `SUM(${numericTableCalcArg}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'window_avg') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const start = parseWindowBound(node.args?.[1], true)
    const end = parseWindowBound(node.args?.[2], false)
    if (!start.ok || !end.ok) {
      errs.push({ message: 'Unsupported WINDOW_AVG bounds; defaulting to full partition', start: node.span.start, end: node.span.end })
    }
    const frame = renderWindowFrame(start.ok ? start.value : null, end.ok ? end.value : null)
    return { sql: `AVG(${numericTableCalcArg}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'window_min') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const start = parseWindowBound(node.args?.[1], true)
    const end = parseWindowBound(node.args?.[2], false)
    if (!start.ok || !end.ok) {
      errs.push({ message: 'Unsupported WINDOW_MIN bounds; defaulting to full partition', start: node.span.start, end: node.span.end })
    }
    const frame = renderWindowFrame(start.ok ? start.value : null, end.ok ? end.value : null)
    return { sql: `MIN(${a0TableCalc}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'window_max') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const start = parseWindowBound(node.args?.[1], true)
    const end = parseWindowBound(node.args?.[2], false)
    if (!start.ok || !end.ok) {
      errs.push({ message: 'Unsupported WINDOW_MAX bounds; defaulting to full partition', start: node.span.start, end: node.span.end })
    }
    const frame = renderWindowFrame(start.ok ? start.value : null, end.ok ? end.value : null)
    return { sql: `MAX(${a0TableCalc}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'window_count') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const start = parseWindowBound(node.args?.[1], true)
    const end = parseWindowBound(node.args?.[2], false)
    if (!start.ok || !end.ok) {
      errs.push({ message: 'Unsupported WINDOW_COUNT bounds; defaulting to full partition', start: node.span.start, end: node.span.end })
    }
    const frame = renderWindowFrame(start.ok ? start.value : null, end.ok ? end.value : null)
    return { sql: `COUNT(${a0TableCalc}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'running_avg') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const frame = renderWindowFrame(null, 0)
    return { sql: `AVG(${numericTableCalcArg}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'running_count') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const frame = renderWindowFrame(null, 0)
    return { sql: `COUNT(${a0TableCalc}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'running_min') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const frame = renderWindowFrame(null, 0)
    return { sql: `MIN(${a0TableCalc}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'running_max') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const frame = renderWindowFrame(null, 0)
    return { sql: `MAX(${a0TableCalc}) OVER (__TBLCALC__${frame})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'index') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    return { sql: `ROW_NUMBER() OVER (__TBLCALC__)`, deps, errs, tableCalc: cfg }
  }
  if (name === 'size') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const full = renderWindowFrame(null, null)
    return { sql: `COUNT(*) OVER (__TBLCALC__${full})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'total') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    const full = renderWindowFrame(null, null)
    return { sql: `SUM(${numericTableCalcArg}) OVER (__TBLCALC__${full})`, deps, errs, tableCalc: cfg }
  }
  if (name === 'lookup') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    // LOOKUP(expr, offset) -> LAG/LEAD over the table-calc addressing order.
    // Tableau semantics:
    // - offset < 0 => previous rows (LAG)
    // - offset > 0 => next rows (LEAD)
    // - offset = 0 => current row (identity)
    //
    // NOTE: DuckDB (and most SQL engines) require non-negative offsets for LAG/LEAD.
    // We compile a best-effort CASE expression for non-literal offsets.
    const offsetSql = a1 !== 'NULL' ? String(a1) : '-1'
    const normalizeLiteral = (s: string): string => {
      let t = String(s ?? '').trim()
      // Peel a few layers of wrapping parentheses (common for unary minus like "(- 1)").
      for (let i = 0; i < 3; i++) {
        if (t.startsWith('(') && t.endsWith(')')) t = t.slice(1, -1).trim()
        else break
      }
      t = t.replace(/\s+/g, '')
      if (t.startsWith('+')) t = t.slice(1)
      return t
    }
    const lit = normalizeLiteral(offsetSql)
    const asInt = /^-?\d+$/.test(lit) ? Number.parseInt(lit, 10) : null
    if (asInt !== null && Number.isFinite(asInt)) {
      if (asInt === 0) return { sql: `${a0}`, deps, errs, tableCalc: cfg }
    if (asInt < 0) return { sql: `LAG(${a0TableCalc}, ${Math.abs(asInt)}) OVER (__TBLCALC__)`, deps, errs, tableCalc: cfg }
    return { sql: `LEAD(${a0TableCalc}, ${asInt}) OVER (__TBLCALC__)`, deps, errs, tableCalc: cfg }
    }
    const full = renderWindowFrame(null, null)
    const offsetCompact = normalizeLiteral(offsetSql)
    const isFirstOffset =
      /^1-ROW_NUMBER\(\)OVER\(__TBLCALC__(?:ROWSBETWEENUNBOUNDEDPRECEDINGANDCURRENTROW)?\)$/i.test(offsetCompact)
    const isLastOffset =
      /^COUNT\(\*\)OVER\(__TBLCALC__ROWSBETWEENUNBOUNDEDPRECEDINGANDUNBOUNDEDFOLLOWING\)-ROW_NUMBER\(\)OVER\(__TBLCALC__(?:ROWSBETWEENUNBOUNDEDPRECEDINGANDCURRENTROW)?\)$/i.test(offsetCompact)
    if (isFirstOffset) {
      return {
        sql: `FIRST_VALUE(${a0TableCalc}) OVER (__TBLCALC__${full})`,
        deps,
        errs,
        tableCalc: cfg,
      }
    }
    if (isLastOffset) {
      return {
        sql: `LAST_VALUE(${a0TableCalc}) OVER (__TBLCALC__${full})`,
        deps,
        errs,
        tableCalc: cfg,
      }
    }
    return {
      sql: `CASE
        WHEN (${offsetSql}) < 0 THEN LAG(${a0TableCalc}, ABS(${offsetSql})) OVER (__TBLCALC__)
        WHEN (${offsetSql}) > 0 THEN LEAD(${a0TableCalc}, (${offsetSql})) OVER (__TBLCALC__)
        ELSE ${a0}
      END`,
      deps,
      errs,
      tableCalc: cfg,
    }
  }
  if (name === 'previous_value') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    // PREVIOUS_VALUE(expr) -> LAG(expr, 1) OVER (...)
    return { sql: `LAG(${a0TableCalc}, 1) OVER (__TBLCALC__)`, deps, errs, tableCalc: cfg }
  }
  if (name === 'first') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    // Tableau: FIRST() returns the relative offset to the first row in the partition.
    // Values: 0 (first row), -1 (second row), -2 (third row), ...
    // Equivalent: 1 - ROW_NUMBER()
    return { sql: `(1 - ROW_NUMBER() OVER (__TBLCALC__))`, deps, errs, tableCalc: cfg }
  }
  if (name === 'last') {
    const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
    // Tableau: LAST() returns the relative offset to the last row in the partition.
    // Values: 0 (last row), 1 (second-to-last), ... (n-1 for first row)
    // Equivalent: COUNT(*) - ROW_NUMBER()
    const full = renderWindowFrame(null, null)
    return { sql: `(COUNT(*) OVER (__TBLCALC__${full}) - ROW_NUMBER() OVER (__TBLCALC__))`, deps, errs, tableCalc: cfg }
  }

  // Fallback: treat as DuckDB function name and compile as-is.
  return { sql: `${String(node.name).toUpperCase()}(${args.map(a => a.duckdb_sql).join(', ')})`, deps, errs }
}

function ctxTableCalcConfigOrDefault(enabled: boolean): TableCalcConfig {
  return {
    enabled,
    compute_using: {
      addressing: [],
      partitioning: [],
      order_by: [],
      frame: null,
    },
  }
}

function compileSetPredicate(args: {
  setName: string
  ctx: ResolveContext
  st: CompileState
  span: { start: number; end: number }
  scopeExprOverride?: string | null
  setStack?: Set<string>
}): { sql: string | null; deps: CalcDependency[]; errs: CompileError[] } {
  const { setName, ctx, st, span, scopeExprOverride } = args
  const deps: CalcDependency[] = []
  const errs: CompileError[] = []
  const setDef = findSetDef(ctx, setName)
  if (!setDef) return { sql: '(FALSE)', deps, errs }

  const key = normalizeSetName(String(setDef?.name ?? setName))
  const stack = args.setStack ?? new Set<string>()
  if (key && stack.has(key)) return { sql: '(FALSE)', deps, errs }
  if (key) stack.add(key)

  let scopeExpr = scopeExprOverride ? String(scopeExprOverride).trim() : ''
  if (!scopeExpr) {
    const scopeField = String(setDef?.scope_field ?? (setDef as any)?.scope?.field_id ?? '').trim()
    if (!scopeField) return { sql: '(FALSE)', deps, errs }
    const rr = resolveFieldOrParamOrSet(ctx, { source: null, field: scopeField })
    deps.push(...rr.dependencies)
    if (rr.errors.length) return { sql: '(FALSE)', deps, errs }
    if (isSetSentinel(rr.sql).is) return { sql: '(FALSE)', deps, errs }
    scopeExpr = rr.sql
  }

  const literal = (v: any): string => {
    if (v === null || v === undefined) return 'NULL'
    if (typeof v === 'number') return String(v)
    if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE'
    return quoteStringLiteral(String(v))
  }

  const members = Array.isArray(setDef?.members) ? setDef.members : []
  const excluded = Array.isArray(setDef?.excluded_members) ? setDef.excluded_members : []
  if (members.length || excluded.length) {
    const parts: string[] = []
    if (members.length) {
      const lits = members.map(literal).join(', ')
      parts.push(`${scopeExpr} IN (${lits})`)
    }
    if (excluded.length) {
      const lits = excluded.map(literal).join(', ')
      parts.push(`(${scopeExpr} IS NULL OR ${scopeExpr} NOT IN (${lits}))`)
    }
    const sql = parts.length === 1 ? parts[0] : `(${parts.join(' AND ')})`
    return { sql, deps, errs }
  }

  const comb = setDef?.combination
  const operands: string[] = Array.isArray(comb?.operands) ? (comb.operands as any[]) : []
  if (operands.length) {
    const op = String(comb?.op ?? '').trim().toLowerCase()
    const parts: string[] = []
    for (const nm of operands) {
      const pred = compileSetPredicate({
        setName: String(nm ?? ''),
        ctx,
        st,
        span,
        scopeExprOverride: scopeExpr,
        setStack: new Set(stack),
      })
      deps.push(...pred.deps)
      if (pred.sql) parts.push(pred.sql)
    }
    if (parts.length) {
      if (op === 'intersect') return { sql: `(${parts.join(' AND ')})`, deps, errs }
      if (op === 'except') {
        if (parts.length === 1) return { sql: parts[0], deps, errs }
        return { sql: `(${parts[0]} AND NOT (${parts.slice(1).join(' OR ')}))`, deps, errs }
      }
      return { sql: `(${parts.join(' OR ')})`, deps, errs }
    }
  }

  const condFormula = String(setDef?.condition?.formula ?? '').trim()
  if (condFormula) {
    const parsed = parseTableauCalc(condFormula)
    if (parsed.ast) {
      const compiled = compileAstInner(parsed.ast, ctx, st)
      deps.push(...compiled.dependencies)
      if (compiled.duckdb_sql) return { sql: `(${compiled.duckdb_sql})`, deps, errs }
    }
  }

  return { sql: '(FALSE)', deps, errs }
}

function compileAstInner(ast: CalcAst, ctx: ResolveContext, st: CompileState): CompileResult {
  const errors: CompileError[] = []
  const deps: CalcDependency[] = []

  const tc = typecheckCalcAst(ast)

  // Best-effort: track flags from any inlined calculated-field refs.
  let nestedIsAgg = false
  let nestedIsTableCalc = false
  let nestedResultType: string | null = null

  const calcFields: CalculatedField[] = Array.isArray((ctx.spec.data_pane as any)?.calculated_fields)
    ? (((ctx.spec.data_pane as any).calculated_fields) as any[])
    : []

  const scopeForCalc = (calc: any): CalcScope => {
    const s = (calc as any)?.scope
    if (s && typeof s === 'object' && (s as any).kind === 'source' && String((s as any).source_id || '').trim()) {
      return { kind: 'source', source_id: String((s as any).source_id || '').trim() }
    }
    const sid = String((calc as any)?.source_id || '').trim()
    if (sid) return { kind: 'source', source_id: sid }
    return { kind: 'global' }
  }

  const findCalculatedField = (ref: { source?: string | null; field: string }): { calc: CalculatedField | null; errs: string[] } => {
    const outErrs: string[] = []
    const raw = String(ref.field || '')
    const cleaned = cleanBracketedFieldRef(raw) || raw
    const key = normalizeKey(cleaned)
    const tokenKey = normalizeKey(normalizeTableauFieldToken(cleaned))
    const tableauKey = normalizeTableauFieldKey(cleaned)
    if (!key && !tokenKey && !tableauKey) return { calc: null, errs: [] }

    const byExact = calcFields.filter((c) => {
      const nm0 = String((c as any)?.name ?? '')
      const nm = cleanBracketedFieldRef(nm0) || nm0
      const k0 = normalizeKey(nm)
      return key && k0 === key
    })
    const byToken = tokenKey
      ? calcFields.filter((c) => {
          const nm0 = String((c as any)?.name ?? '')
          const nm = cleanBracketedFieldRef(nm0) || nm0
          const k1 = normalizeKey(normalizeTableauFieldToken(nm))
          return k1 === tokenKey
        })
      : []
    const byTableau = tableauKey
      ? calcFields.filter((c) => {
          const nm0 = String((c as any)?.name ?? '')
          const nm = cleanBracketedFieldRef(nm0) || nm0
          const k2 = normalizeTableauFieldKey(nm)
          return k2 === tableauKey
        })
      : []
    const byName = byExact.length ? byExact : (byToken.length ? byToken : byTableau)
    if (byName.length === 0) return { calc: null, errs: [] }

    const sources: any[] = Array.isArray((ctx.spec.data_pane as any)?.sources) ? (((ctx.spec.data_pane as any).sources) as any[]) : []
    const suffix = (s: string) => {
      const parts = String(s || '').split('.').map((p) => p.trim()).filter(Boolean)
      return parts.length ? parts[parts.length - 1] : String(s || '')
    }

    const resolveSourceId = (): { ok: boolean; sourceId: string | null } => {
      const srcNameKey = normalizeTableauDatasourceKey(String(ref.source || '')) || normalizeKey(String(ref.source || ''))
      if (!srcNameKey) return { ok: true, sourceId: null }
      const candidates = sources.filter((s) => {
        const aliases = [
          ...((Array.isArray((s as any)?.aliases) ? (s as any).aliases : []) as any[]),
          ...((Array.isArray((s as any)?.source_aliases) ? (s as any).source_aliases : []) as any[]),
        ]
        const sourceNames = [
          String(s?.name ?? ''),
          String(s?.id ?? ''),
          String((s as any)?.reference ?? ''),
          suffix(String((s as any)?.reference ?? '')),
          ...aliases.map((alias) => String(alias ?? '')),
          ...aliases.map((alias) => suffix(String(alias ?? ''))),
        ]
        return sourceNames.some((nameRaw) => {
          const key = normalizeTableauDatasourceKey(nameRaw) || normalizeKey(nameRaw)
          return key === srcNameKey
        })
      })
      if (candidates.length === 0) {
        // Best-effort fallback: if there's only one attached source, treat it as the match.
        if (sources.length === 1) {
          const sid = String(sources[0]?.id ?? '').trim()
          return { ok: true, sourceId: sid || null }
        }
        return { ok: false, sourceId: null }
      }
      if (candidates.length > 1) {
        outErrs.push(`Ambiguous source name for calculated field: ${String(ref.source)}`)
        return { ok: false, sourceId: null }
      }
      const sid = String(candidates[0]?.id ?? '').trim()
      return { ok: true, sourceId: sid || null }
    }

    const src = ref.source ? resolveSourceId() : { ok: true, sourceId: null }
    if (ref.source && !src.ok) {
      outErrs.push(`Unknown source for calculated field reference: ${String(ref.source)}`)
      return { calc: null, errs: outErrs }
    }

    const inSource = (c: any, sid: string | null): boolean => {
      if (!sid) return true
      const sc = scopeForCalc(c)
      return sc.kind === 'source' && normalizeKey(sc.source_id) === normalizeKey(sid)
    }

    const pickUnique = (cands: any[]): any | null => {
      if (cands.length === 0) return null
      if (cands.length === 1) return cands[0]
      outErrs.push(`Ambiguous calculated field reference: ${ref.field}`)
      return null
    }

    // If explicitly qualified, restrict to that source.
    if (ref.source) {
      const narrowed = byName.filter((c) => inSource(c, src.sourceId))
      const picked = pickUnique(narrowed)
      return { calc: picked || null, errs: outErrs }
    }

    // Unqualified: prefer current scope source if present.
    if (ctx.scope.kind === 'source') {
      const sid = String((ctx.scope as any).source_id ?? '').trim()
      const scoped = byName.filter((c) => inSource(c, sid || null))
      const picked = pickUnique(scoped.length ? scoped : byName)
      return { calc: picked || null, errs: outErrs }
    }

    const picked = pickUnique(byName)
    return { calc: picked || null, errs: outErrs }
  }

  const compileCalculatedFieldRef = (calc: CalculatedField, span: { start: number; end: number }): CompileResult => {
    const id = String((calc as any)?.id ?? '').trim() || normalizeKey(String((calc as any)?.name ?? ''))
    if (!id) {
      return {
        duckdb_sql: 'NULL',
        dependencies: [],
        result_type: 'unknown',
        is_aggregate: false,
        is_table_calc: false,
        errors: [{ message: 'Invalid calculated field (missing id)', start: span.start, end: span.end }],
      }
    }

    if (st.calc_stack.includes(id)) {
      return {
        duckdb_sql: 'NULL',
        dependencies: [{ kind: 'calc', id }],
        result_type: 'unknown',
        is_aggregate: false,
        is_table_calc: false,
        errors: [{ message: `Circular calculated field reference: ${String((calc as any)?.name ?? id)}`, start: span.start, end: span.end }],
      }
    }
    if (st.calc_stack.length >= st.max_calc_depth) {
      return {
        duckdb_sql: 'NULL',
        dependencies: [{ kind: 'calc', id }],
        result_type: 'unknown',
        is_aggregate: false,
        is_table_calc: false,
        errors: [{ message: `Calculated field expansion exceeded max depth (${st.max_calc_depth})`, start: span.start, end: span.end }],
      }
    }

    const cached = st.calc_cache.get(id)
    if (cached) return cached

    const scope = scopeForCalc(calc)
    const lang = String((calc as any)?.language ?? 'tableau').trim().toLowerCase()
    const expr = String((calc as any)?.expression ?? (calc as any)?.formula ?? '').trim()
    if (!expr) {
      const empty: CompileResult = {
        duckdb_sql: 'NULL',
        dependencies: [{ kind: 'calc', id }],
        result_type: 'unknown',
        is_aggregate: false,
        is_table_calc: false,
        errors: [{ message: `Calculated field "${String((calc as any)?.name ?? id)}" has no expression`, start: span.start, end: span.end }],
      }
      st.calc_cache.set(id, empty)
      return empty
    }

    // SQL-mode calcs: treat expression as DuckDB SQL and inline directly.
    if (lang !== 'tableau') {
      const out: CompileResult = {
        duckdb_sql: `(${expr})`,
        dependencies: [{ kind: 'calc', id }],
        result_type: String((calc as any)?.result_type ?? 'unknown'),
        is_aggregate: false,
        is_table_calc: false,
        errors: [],
      }
      st.calc_cache.set(id, out)
      return out
    }

    const parsed = parseTableauCalc(expr)
    const parseErrors: CompileError[] = parsed.errors.map((e) => ({
      message: `In "${String((calc as any)?.name ?? id)}": ${e.message}`,
      start: span.start,
      end: span.end,
    }))
    if (!parsed.ast) {
      const out: CompileResult = {
        duckdb_sql: 'NULL',
        dependencies: [{ kind: 'calc', id }],
        result_type: 'unknown',
        is_aggregate: false,
        is_table_calc: false,
        errors: parseErrors.length ? parseErrors : [{ message: `Parse failed for calculated field "${String((calc as any)?.name ?? id)}"`, start: span.start, end: span.end }],
      }
      st.calc_cache.set(id, out)
      return out
    }

    st.calc_stack.push(id)
    const nestedCtx: ResolveContext = { spec: ctx.spec, scope }
    const compiled = compileAstInner(parsed.ast, nestedCtx, st)
    st.calc_stack.pop()

    // Inline table-calcs if config is present on the referenced calc.
    let duckdbSql = compiled.duckdb_sql
    const tcCfg: TableCalcConfig | undefined = (calc as any)?.table_calc ?? compiled.table_calc
    if (tcCfg?.enabled && (duckdbSql.includes('__TBLCALC__') || duckdbSql.includes('__TBLCALC_PARTITION__'))) {
      // Full OVER clause replacement (PARTITION BY + ORDER BY + optional frame)
      if (duckdbSql.includes('__TBLCALC__')) {
        const hasInlineFrame = /__TBLCALC__\s+(ROWS|RANGE)\b/i.test(duckdbSql)
        const cfg = hasInlineFrame ? { ...tcCfg.compute_using, frame: null } : tcCfg.compute_using
        const over = renderOverClause(cfg)
        const clause = over.over || ''
        duckdbSql = rewriteLinearPreviousValueSequence(duckdbSql, clause)
        duckdbSql = duckdbSql.replaceAll('__TBLCALC__', clause)
        if (over.errors.length) {
          compiled.errors.push({ message: `In "${String((calc as any)?.name ?? id)}": ${over.errors.join(' | ')}`, start: span.start, end: span.end })
        }
      }

      // Partition-only replacement (used by table calcs that embed their own ORDER BY expression)
      if (duckdbSql.includes('__TBLCALC_PARTITION__')) {
        const cfgPart = { ...tcCfg.compute_using, addressing: [], order_by: [], frame: null }
        const overPart = renderOverClause(cfgPart)
        const clausePart = overPart.over || ''
        duckdbSql = duckdbSql.replaceAll('__TBLCALC_PARTITION__', clausePart)
        if (overPart.errors.length) {
          compiled.errors.push({ message: `In "${String((calc as any)?.name ?? id)}": ${overPart.errors.join(' | ')}`, start: span.start, end: span.end })
        }
      }
    }

    const out: CompileResult = {
      duckdb_sql: `(${duckdbSql})`,
      dependencies: [{ kind: 'calc', id }, ...compiled.dependencies],
      result_type: compiled.result_type,
      is_aggregate: compiled.is_aggregate,
      is_table_calc: compiled.is_table_calc,
      errors: [...parseErrors, ...compiled.errors],
      table_calc: tcCfg ?? compiled.table_calc,
    }
    st.calc_cache.set(id, out)
    return out
  }

  const AGG_CALLS = new Set(['sum', 'avg', 'min', 'max', 'count', 'countd', 'attr', 'agg'])
  const TABLE_CALC_CALLS = new Set([
    'running_sum',
    'running_avg',
    'running_count',
    'running_min',
    'running_max',
    'window_sum',
    'window_avg',
    'window_count',
    'window_min',
    'window_max',
    'index',
    'size',
    'total',
    'rank',
    'rank_dense',
    'lookup',
    'previous_value',
    'first',
    'last',
  ])

  const analyzeAggMix = (node: CalcAst, inAggContext: boolean): { hasAgg: boolean; hasRowOutsideAgg: boolean } => {
    if (node.kind === 'lod') {
      // LOD expressions are aggregate scope.
      let hasRow = false
      for (const d of node.dimensions) {
        const r = analyzeAggMix(d, true)
        hasRow = hasRow || r.hasRowOutsideAgg
      }
      const e = analyzeAggMix(node.expression, true)
      return { hasAgg: true, hasRowOutsideAgg: hasRow || e.hasRowOutsideAgg }
    }

    if (node.kind === 'call') {
      const nm = String(node.name || '').trim().toLowerCase()
      const isAggFn = AGG_CALLS.has(nm)
      const isTableFn = TABLE_CALC_CALLS.has(nm)
      const nextInAgg = inAggContext || isAggFn || isTableFn
      let hasAgg = isAggFn || isTableFn
      let hasRow = false
      for (const a of node.args || []) {
        const r = analyzeAggMix(a, nextInAgg)
        hasAgg = hasAgg || r.hasAgg
        hasRow = hasRow || r.hasRowOutsideAgg
      }
      return { hasAgg, hasRowOutsideAgg: hasRow }
    }

    if (node.kind === 'field_ref') {
      // Resolve to understand whether this is a param/set/physical field or a calculated field.
      const rr = resolveFieldOrParamOrSet(ctx, { source: node.source ?? null, field: node.field })
      if (rr.errors.length === 0) {
        const isPhysical = rr.dependencies.some((d) => (d as any)?.kind === 'field')
        return { hasAgg: false, hasRowOutsideAgg: Boolean(isPhysical && !inAggContext) }
      }
      const hit = findCalculatedField({ source: node.source ?? null, field: node.field })
      if (hit.calc) {
        const compiled = compileCalculatedFieldRef(hit.calc, node.span)
        // Treat calc refs as aggregate iff the referenced calc is aggregate.
        return { hasAgg: compiled.is_aggregate || compiled.is_table_calc, hasRowOutsideAgg: Boolean(!inAggContext && !compiled.is_aggregate && !compiled.is_table_calc) }
      }
      // Unknown field: assume row-level (so we surface a useful mix error when combined with aggregates)
      return { hasAgg: false, hasRowOutsideAgg: Boolean(!inAggContext) }
    }

    if (node.kind === 'binary') {
      const l = analyzeAggMix(node.left, inAggContext)
      const r = analyzeAggMix(node.right, inAggContext)
      return { hasAgg: l.hasAgg || r.hasAgg, hasRowOutsideAgg: l.hasRowOutsideAgg || r.hasRowOutsideAgg }
    }
    if (node.kind === 'unary') {
      return analyzeAggMix(node.expr, inAggContext)
    }
    if (node.kind === 'if') {
      let hasAgg = false
      let hasRow = false
      for (const b of node.branches) {
        const w = analyzeAggMix(b.when, inAggContext)
        const t = analyzeAggMix(b.then, inAggContext)
        hasAgg = hasAgg || w.hasAgg || t.hasAgg
        hasRow = hasRow || w.hasRowOutsideAgg || t.hasRowOutsideAgg
      }
      if (node.else_expr) {
        const e = analyzeAggMix(node.else_expr, inAggContext)
        hasAgg = hasAgg || e.hasAgg
        hasRow = hasRow || e.hasRowOutsideAgg
      }
      return { hasAgg, hasRowOutsideAgg: hasRow }
    }
    if (node.kind === 'case') {
      let hasAgg = false
      let hasRow = false
      if (node.base) {
        const b = analyzeAggMix(node.base, inAggContext)
        hasAgg = hasAgg || b.hasAgg
        hasRow = hasRow || b.hasRowOutsideAgg
      }
      for (const w of node.whens) {
        const ww = analyzeAggMix(w.when, inAggContext)
        const tt = analyzeAggMix(w.then, inAggContext)
        hasAgg = hasAgg || ww.hasAgg || tt.hasAgg
        hasRow = hasRow || ww.hasRowOutsideAgg || tt.hasRowOutsideAgg
      }
      if (node.else_expr) {
        const e = analyzeAggMix(node.else_expr, inAggContext)
        hasAgg = hasAgg || e.hasAgg
        hasRow = hasRow || e.hasRowOutsideAgg
      }
      return { hasAgg, hasRowOutsideAgg: hasRow }
    }

    return { hasAgg: false, hasRowOutsideAgg: false }
  }

  const compileNode = (): { sql: string; tableCalc?: TableCalcConfig } => {
    if (ast.kind === 'literal') {
      if (ast.literal_kind === 'null') return { sql: 'NULL' }
      if (ast.literal_kind === 'bool') return { sql: ast.value ? 'TRUE' : 'FALSE' }
      if (ast.literal_kind === 'number') return { sql: String(ast.value) }
      return { sql: quoteStringLiteral(String(ast.value ?? '')) }
    }
    if (ast.kind === 'field_ref') {
      // 1) Try physical/param/set resolution.
      const r = resolveFieldOrParamOrSet(ctx, { source: ast.source ?? null, field: ast.field })
      if (r.errors.length === 0) {
        const sentinel = isSetSentinel(r.sql)
        if (sentinel.is && sentinel.name) {
          const pred = compileSetPredicate({
            setName: sentinel.name,
            ctx,
            st,
            span: ast.span,
          })
          deps.push(...r.dependencies, ...pred.deps)
          errors.push(...pred.errs)
          return { sql: pred.sql ?? 'FALSE' }
        }
        deps.push(...r.dependencies)
        return { sql: r.sql }
      }

      // 2) Fallback: calculated field resolution (inline).
      const hit = findCalculatedField({ source: ast.source ?? null, field: ast.field })
      if (hit.calc) {
        for (const em of hit.errs) {
          errors.push({ message: em, start: ast.span.start, end: ast.span.end })
        }
        const compiled = compileCalculatedFieldRef(hit.calc, ast.span)
        deps.push(...compiled.dependencies)
        errors.push(...compiled.errors)
        nestedIsAgg = nestedIsAgg || compiled.is_aggregate
        nestedIsTableCalc = nestedIsTableCalc || compiled.is_table_calc
        nestedResultType = compiled.result_type || nestedResultType
        return { sql: compiled.duckdb_sql, tableCalc: compiled.table_calc }
      }

      deps.push(...r.dependencies)
      for (const msg of r.errors) {
        errors.push({ message: msg, start: ast.span.start, end: ast.span.end })
      }
      return { sql: r.sql }
    }
    if (ast.kind === 'unary') {
      const inner = compileAstInner(ast.expr, ctx, st)
      deps.push(...inner.dependencies)
      errors.push(...inner.errors)
      const op = ast.op === 'not' ? 'NOT' : ast.op
      return { sql: `(${op} ${inner.duckdb_sql})` }
    }
      if (ast.kind === 'binary') {
      const compilePreviousValueLinearRecurrence = (): { sql: string; tableCalc: TableCalcConfig } | null => {
        if (ast.op !== '+' && ast.op !== '-') return null
        const cfg: TableCalcConfig = ctxTableCalcConfigOrDefault(true)
        const row = `TRY_CAST(ROW_NUMBER() OVER (__TBLCALC__) AS DOUBLE)`
        const build = (seedAst: CalcAst, deltaAst: CalcAst, sign: '+' | '-'): { sql: string; tableCalc: TableCalcConfig } => {
          const seed = compileAstInner(seedAst, ctx, st)
          const delta = compileAstInner(deltaAst, ctx, st)
          deps.push(...seed.dependencies, ...delta.dependencies)
          errors.push(...seed.errors, ...delta.errors)
          return {
            sql: `(TRY_CAST(${seed.duckdb_sql} AS DOUBLE) ${sign} (${row} * TRY_CAST(${delta.duckdb_sql} AS DOUBLE)))`,
            tableCalc: cfg,
          }
        }
        if (isPreviousValueAst(ast.left) && ast.left.args?.[0]) {
          return build(ast.left.args[0], ast.right, ast.op === '+' ? '+' : '-')
        }
        if (ast.op === '+' && isPreviousValueAst(ast.right) && ast.right.args?.[0]) {
          return build(ast.right.args[0], ast.left, '+')
        }
        return null
      }

      const previousValueLinearRecurrence = compilePreviousValueLinearRecurrence()
      if (previousValueLinearRecurrence) return previousValueLinearRecurrence

      const l = compileAstInner(ast.left, ctx, st)
      const r = compileAstInner(ast.right, ctx, st)
      deps.push(...l.dependencies, ...r.dependencies)
      errors.push(...l.errors, ...r.errors)

      if (ast.op === 'in') {
        // Special-case: right side is a Set reference.
        const sentinel = isSetSentinel(r.duckdb_sql)
        if (sentinel.is && sentinel.name) {
          const pred = compileSetPredicate({
            setName: sentinel.name,
            ctx,
            st,
            span: ast.right.span,
            scopeExprOverride: l.duckdb_sql,
          })
          deps.push(...pred.deps)
          errors.push(...pred.errs)
          return { sql: pred.sql ?? '(FALSE)' }
        }
      }

      if (ast.op === '^') {
        return { sql: `POWER(${numericAggArg(l.duckdb_sql)}, ${numericAggArg(r.duckdb_sql)})` }
      }

      // Tableau supports string concatenation via "+" when either side is stringy.
      // We compile to `||` to preserve NULL-propagation semantics.
      if (ast.op === '+') {
        const lt = typecheckCalcAst(ast.left).result_type
        const rt = typecheckCalcAst(ast.right).result_type
        if (lt === 'string' || rt === 'string') {
          return { sql: `(CAST(${l.duckdb_sql} AS VARCHAR) || CAST(${r.duckdb_sql} AS VARCHAR))` }
        }
      }

      // Tableau date arithmetic semantics:
      // - DATE/TIMESTAMP +/- N => add/subtract N days.
      // DuckDB does not support TIMESTAMP +/- INTEGER directly, so compile to INTERVAL math.
      if (ast.op === '+' || ast.op === '-') {
      let lt = typecheckCalcAst(ast.left).result_type
      let rt = typecheckCalcAst(ast.right).result_type

      // Typecheck is AST-only and cannot infer param types, so explicitly treat typed
      // parameters as date-ish when present in the VisualSpec.
      const lp = paramTypeOfFieldRef(ctx, ast.left)
      const rp = paramTypeOfFieldRef(ctx, ast.right)
      if (lp) lt = lp
      if (rp) rt = rp

        const isDateish = (t?: string | null) => t === 'date' || t === 'timestamp'
        const isNumeric = (t?: string | null) => t === 'number'
        if (isDateish(lt) && isNumeric(rt)) {
          const days = numericAggArg(r.duckdb_sql)
          const lhsDate = coerceDateAddOperand(l.duckdb_sql, ast.left)
          return { sql: `(${lhsDate} ${ast.op} (${days} * INTERVAL 1 DAY))` }
        }
        if (ast.op === '+' && isNumeric(lt) && isDateish(rt)) {
          const days = numericAggArg(l.duckdb_sql)
          const rhsDate = coerceDateAddOperand(r.duckdb_sql, ast.right)
          return { sql: `(${rhsDate} + (${days} * INTERVAL 1 DAY))` }
        }
      }

      // Tableau division-by-zero semantics are NULL (best-effort).
      // Keep SQL simple when denominator is a non-zero literal.
      if (ast.op === '/') {
        const lNum = numericAggArg(l.duckdb_sql)
        const rNum = numericAggArg(r.duckdb_sql)
        const denomLiteral = (() => {
          if (ast.right.kind === 'literal' && ast.right.literal_kind === 'number') return Number(ast.right.value)
          if (ast.right.kind === 'unary' && ast.right.op === '+' && ast.right.expr?.kind === 'literal' && ast.right.expr.literal_kind === 'number') {
            return Number(ast.right.expr.value)
          }
          if (ast.right.kind === 'unary' && ast.right.op === '-' && ast.right.expr?.kind === 'literal' && ast.right.expr.literal_kind === 'number') {
            return -Number(ast.right.expr.value)
          }
          return null
        })()
        if (denomLiteral !== null && Number.isFinite(denomLiteral)) {
          if (denomLiteral === 0) return { sql: 'NULL' }
          return { sql: `(${lNum} / ${rNum})` }
        }

        return {
          sql: `(CASE
            WHEN (${rNum}) IS NULL OR (${rNum}) = 0 THEN NULL
            ELSE (${lNum}) / (${rNum})
          END)`.replace(/\s+/g, ' ').trim(),
        }
      }

      // Keep arithmetic strongly numeric so text-backed measure columns still compile.
      if (ast.op === '+' || ast.op === '-' || ast.op === '*') {
        const lNum = numericAggArg(l.duckdb_sql)
        const rNum = numericAggArg(r.duckdb_sql)
        return { sql: `(${lNum} ${ast.op} ${rNum})` }
      }

      const op = ast.op === 'and' ? 'AND' : ast.op === 'or' ? 'OR' : ast.op.toUpperCase()
      return { sql: `(${l.duckdb_sql} ${op} ${r.duckdb_sql})` }
    }
    if (ast.kind === 'if') {
      const r = compileIfExpr(ast, ctx, st)
      deps.push(...r.deps)
      errors.push(...r.errs)
      return { sql: `(${r.sql})` }
    }
    if (ast.kind === 'case') {
      const r = compileCaseExpr(ast, ctx, st)
      deps.push(...r.deps)
      errors.push(...r.errs)
      return { sql: `(${r.sql})` }
    }
    if (ast.kind === 'call') {
      const r = compileCall(ast, ctx, st)
      deps.push(...r.deps)
      errors.push(...r.errs)
      return { sql: `(${r.sql})`, tableCalc: r.tableCalc }
    }
    if (ast.kind === 'lod') {
      const r = compileLodExpr(ast, ctx, st)
      deps.push(...r.deps)
      errors.push(...r.errs)
      return { sql: r.sql }
    }
    return { sql: 'NULL' }
  }

  const compiled = compileNode()

  const isAgg = tc.is_aggregate || nestedIsAgg
  const isTable = tc.is_table_calc || nestedIsTableCalc
  const resultType = nestedResultType ?? tc.result_type

  // Tableau semantic validation (best-effort): don’t allow mixing aggregate and non-aggregate.
  // This is a critical parity rule; we surface it early in authoring to avoid generating invalid SQL.
  const mix = analyzeAggMix(ast, false)
  if ((mix.hasAgg || isAgg || isTable) && mix.hasRowOutsideAgg) {
    errors.push({
      message: 'Cannot mix aggregate and non-aggregate arguments in the same expression. Wrap non-aggregate fields in an aggregate (e.g. MIN/ATTR), or remove aggregate functions.',
      start: ast.span.start,
      end: ast.span.end,
    })
  }

  return {
    duckdb_sql: compiled.sql,
    dependencies: deps,
    result_type: resultType,
    is_aggregate: isAgg,
    is_table_calc: isTable,
    errors,
    table_calc: compiled.tableCalc,
  }
}

export function compileAst(ast: CalcAst, ctx: ResolveContext): CompileResult {
  const st: CompileState = { calc_stack: [], calc_cache: new Map(), max_calc_depth: 32 }
  return compileAstInner(ast, ctx, st)
}

export function compileTableauExpressionToDuckdbSql(opts: {
  spec: VisualSpec
  scope: CalcScope
  expression: string
}): CompileResult {
  const parsed = parseTableauCalc(opts.expression)
  const parseErrors: CompileError[] = parsed.errors.map((e) => ({ message: e.message, start: e.span.start, end: e.span.end }))
  if (!parsed.ast) {
    return {
      duckdb_sql: 'NULL',
      dependencies: [],
      result_type: 'unknown',
      is_aggregate: false,
      is_table_calc: false,
      errors: parseErrors.length ? parseErrors : [{ message: 'Parse failed', start: 0, end: 0 }],
    }
  }
  const ctx: ResolveContext = { spec: opts.spec, scope: opts.scope }
  const st: CompileState = { calc_stack: [], calc_cache: new Map(), max_calc_depth: 32 }
  const compiled = compileAstInner(parsed.ast, ctx, st)
  return { ...compiled, errors: [...parseErrors, ...compiled.errors] }
}

/**
 * Compile a LOD expression to DuckDB SQL.
 * 
 * v1 implementation (best-effort):
 * - Prefer compiling LOD as a windowed aggregate over the current query scope.
 *   This avoids needing a "base table" placeholder and makes the expression usable
 *   in both runtime and extract-backed compilation paths.
 *
 * Semantics notes:
 * - FIXED: partition by the specified dimensions
 * - INCLUDE: partition by (viz-level dims ∪ specified dims)
 * - EXCLUDE: partition by (viz-level dims \ specified dims)
 */
function compileLodExpr(ast: LodExpr, ctx: ResolveContext, st: CompileState): { sql: string; deps: CalcDependency[]; errs: CompileError[] } {
  const deps: CalcDependency[] = []
  const errs: CompileError[] = []

  const dedupe = (items: string[]) => {
    const out: string[] = []
    const seen = new Set<string>()
    for (const it of items) {
      const k = String(it || '').trim()
      if (!k) continue
      if (seen.has(k)) continue
      seen.add(k)
      out.push(k)
    }
    return out
  }

  // Compile dimension expressions (used for partitioning).
  const lodDimExprs: string[] = []
  for (const dim of ast.dimensions) {
    const r = compileAstInner(dim, ctx, st)
    deps.push(...r.dependencies)
    errs.push(...r.errors)
    lodDimExprs.push(r.duckdb_sql)
  }

  const vizDimensions = extractVizDimensions(ctx.spec)
  const vizDimExprs: string[] = []
  for (const dim of vizDimensions) {
    const r = compileAstInner(dim, ctx, st)
    deps.push(...r.dependencies)
    errs.push(...r.errors)
    vizDimExprs.push(r.duckdb_sql)
  }

  let partitionDims: string[] = []
  if (ast.lod_type === 'fixed') {
    partitionDims = dedupe(lodDimExprs)
  } else if (ast.lod_type === 'include') {
    partitionDims = dedupe([...vizDimExprs, ...lodDimExprs])
  } else {
    const exclude = new Set(dedupe(lodDimExprs))
    partitionDims = dedupe(vizDimExprs).filter((d) => !exclude.has(d))
  }

  const overClause = partitionDims.length ? `PARTITION BY ${partitionDims.join(', ')}` : ''

  const compileLodValueExpr = (n: CalcAst): { sql: string; deps: CalcDependency[]; errs: CompileError[] } => {
    const outDeps: CalcDependency[] = []
    const outErrs: CompileError[] = []

    const compileNode = (node: CalcAst): string => {
      if (node.kind === 'literal') {
        if (node.literal_kind === 'null') return 'NULL'
        if (node.literal_kind === 'bool') return node.value ? 'TRUE' : 'FALSE'
        if (node.literal_kind === 'number') return String(node.value)
        return quoteStringLiteral(String(node.value ?? ''))
      }
      if (node.kind === 'field_ref') {
        // Reuse the main compiler for field refs so calculated fields can be inlined.
        const compiled = compileAstInner(node, ctx, st)
        outDeps.push(...compiled.dependencies)
        outErrs.push(...compiled.errors)
        return compiled.duckdb_sql
      }
      if (node.kind === 'unary') {
        const inner = compileNode(node.expr)
        const op = node.op === 'not' ? 'NOT' : node.op
        return `(${op} ${inner})`
      }
      if (node.kind === 'binary') {
        const l = compileNode(node.left)
        const r = compileNode(node.right)
        // Preserve the Set sentinel behavior for "IN [Set]".
        if (node.op === 'in') {
          const sentinel = isSetSentinel(r)
          if (sentinel.is && sentinel.name) {
            const pred = compileSetPredicate({
              setName: sentinel.name,
              ctx,
              st,
              span: node.right.span,
              scopeExprOverride: l,
            })
            outDeps.push(...pred.deps)
            outErrs.push(...pred.errs)
            return pred.sql ?? '(FALSE)'
          }
        }

        // Tableau supports string concatenation via "+" when either side is stringy.
        // We compile to `||` to preserve NULL-propagation semantics.
        if (node.op === '+') {
          const lt = typecheckCalcAst(node.left).result_type
          const rt = typecheckCalcAst(node.right).result_type
          if (lt === 'string' || rt === 'string') {
            return `(CAST(${l} AS VARCHAR) || CAST(${r} AS VARCHAR))`
          }
        }

        if (node.op === '^') {
          return `POWER(${numericAggArg(l)}, ${numericAggArg(r)})`
        }

        // Tableau division-by-zero semantics are NULL (best-effort).
        // Keep SQL simple when denominator is a non-zero literal.
        if (node.op === '/') {
          const denomLiteral = (() => {
            if (node.right.kind === 'literal' && node.right.literal_kind === 'number') return Number(node.right.value)
            if (node.right.kind === 'unary' && node.right.op === '+' && node.right.expr?.kind === 'literal' && node.right.expr.literal_kind === 'number') {
              return Number(node.right.expr.value)
            }
            if (node.right.kind === 'unary' && node.right.op === '-' && node.right.expr?.kind === 'literal' && node.right.expr.literal_kind === 'number') {
              return -Number(node.right.expr.value)
            }
            return null
          })()
          const lNum = numericAggArg(l)
          const rNum = numericAggArg(r)
          if (denomLiteral !== null && Number.isFinite(denomLiteral)) {
            if (denomLiteral === 0) return 'NULL'
            return `(${lNum} / ${rNum})`
          }
          return `(CASE WHEN (${rNum}) IS NULL OR (${rNum}) = 0 THEN NULL ELSE (${lNum}) / (${rNum}) END)`
        }

        if (node.op === '+' || node.op === '-' || node.op === '*') {
          const lNum = numericAggArg(l)
          const rNum = numericAggArg(r)
          return `(${lNum} ${node.op} ${rNum})`
        }

        const op = node.op === 'and' ? 'AND' : node.op === 'or' ? 'OR' : node.op.toUpperCase()
        return `(${l} ${op} ${r})`
      }
      if (node.kind === 'if') {
        const parts: string[] = []
        for (let i = 0; i < node.branches.length; i++) {
          const b = node.branches[i]
          const w = compileNode(b.when)
          const t = compileNode(b.then)
          parts.push(i === 0 ? `CASE WHEN ${w} THEN ${t}` : `WHEN ${w} THEN ${t}`)
        }
        if (node.else_expr) {
          const e = compileNode(node.else_expr)
          parts.push(`ELSE ${e}`)
        }
        parts.push('END')
        return `(${parts.join(' ')})`
      }
      if (node.kind === 'case') {
        const parts: string[] = []
        parts.push('CASE')
        if (node.base) {
          parts.push(compileNode(node.base))
        }
        for (const w of node.whens) {
          const whenSql = compileNode(w.when)
          const thenSql = compileNode(w.then)
          parts.push(`WHEN ${whenSql} THEN ${thenSql}`)
        }
        if (node.else_expr) {
          parts.push(`ELSE ${compileNode(node.else_expr)}`)
        }
        parts.push('END')
        return `(${parts.join(' ')})`
      }
      if (node.kind === 'lod') {
        const r = compileLodExpr(node, ctx, st)
        outDeps.push(...r.deps)
        outErrs.push(...r.errs)
        return r.sql
      }
      if (node.kind === 'call') {
        const callName = String(node.name || '').trim().toLowerCase()
        const compiledArgs = (node.args || []).map((a) => compileNode(a))
        const arg0 = compiledArgs[0] ?? 'NULL'
        const arg1 = compiledArgs[1] ?? 'NULL'
        const arg2 = compiledArgs[2] ?? 'NULL'

        // Helpers / common Tableau wrappers.
        if (callName === 'iif') {
          const caseBranchCoercion = getCaseBranchCoercion(
            node as unknown as CalcAst,
            node.args?.[1] ?? null,
            node.args?.[2] ?? null,
            arg1,
            arg2,
          )
          const thenSql = coerceCaseBranchSql(arg1, caseBranchCoercion)
          const elseSql = coerceCaseBranchSql(arg2, caseBranchCoercion)
          return `CASE WHEN ${arg0} THEN ${thenSql} ELSE ${elseSql} END`
        }
        if (callName === 'isnull') {
          return `(${arg0} IS NULL)`
        }
        if (callName === 'ifnull') {
          return coalesceByType(arg0, arg1, node.args?.[0], node.args?.[1])
        }
        if (callName === 'zn') {
          return `COALESCE(${numericAggArg(arg0)}, 0.0)`
        }

        // Type conversions
        if (callName === 'str') return `CAST(${arg0} AS VARCHAR)`
        if (callName === 'int') return `TRY_CAST(${arg0} AS BIGINT)`
        if (callName === 'float') return `TRY_CAST(${arg0} AS DOUBLE)`
        if (callName === 'date') return `TRY_CAST(${arg0} AS DATE)`
        if (callName === 'datetime') return `TRY_CAST(${arg0} AS TIMESTAMP)`

        // Numeric functions
        if (callName === 'abs') return `ABS(${arg0})`
        if (callName === 'round') return (node.args?.length ?? 0) >= 2 ? `ROUND(${arg0}, ${arg1})` : `ROUND(${arg0})`
        if (callName === 'ceiling' || callName === 'ceil') return `CEIL(${arg0})`
        if (callName === 'floor') return `FLOOR(${arg0})`
        if (callName === 'power') return `POWER(${arg0}, ${arg1})`
        if (callName === 'sqrt') return `SQRT(${arg0})`

        // String functions
        if (callName === 'len' || callName === 'length') return `LENGTH(${arg0})`
        if (callName === 'lower') return `LOWER(${arg0})`
        if (callName === 'upper') return `UPPER(${arg0})`
        if (callName === 'trim') return `TRIM(${arg0})`
        if (callName === 'ltrim') return `LTRIM(${arg0})`
        if (callName === 'rtrim') return `RTRIM(${arg0})`
        if (callName === 'left') return `SUBSTR(${arg0}, 1, TRY_CAST(${arg1} AS BIGINT))`
        if (callName === 'right') return `SUBSTR(${arg0}, (LENGTH(${arg0}) - TRY_CAST(${arg1} AS BIGINT) + 1), TRY_CAST(${arg1} AS BIGINT))`
        if (callName === 'mid') return `SUBSTR(${arg0}, TRY_CAST(${arg1} AS BIGINT), TRY_CAST(${arg2} AS BIGINT))`
        if (callName === 'replace') return `REPLACE(${arg0}, ${arg1}, ${arg2})`
        if (callName === 'contains') return `(POSITION(${arg1} IN ${arg0}) > 0)`
        if (callName === 'startswith') return `STARTS_WITH(${arg0}, ${arg1})`
        if (callName === 'endswith') return `ENDS_WITH(${arg0}, ${arg1})`

        // Date/time
        if (callName === 'today') return buildLocalDateSql()
        if (callName === 'now') return buildLocalTimestampSql()
        if (callName === 'makedate') {
          if ((node.args?.length ?? 0) < 3) {
            outErrs.push({ message: 'MAKEDATE(year, month, day) requires 3 arguments', start: node.span.start, end: node.span.end })
            return 'NULL'
          }
          return `MAKE_DATE(${arg0}, ${arg1}, ${arg2})`
        }
        if (callName === 'dateadd') {
          if ((node.args?.length ?? 0) < 3) {
            outErrs.push({ message: 'DATEADD(date_part, interval, date) requires 3 arguments', start: node.span.start, end: node.span.end })
            return 'NULL'
          }
          const partRaw = (node.args?.[0]?.kind === 'literal' && (node.args[0] as any).literal_kind === 'string')
            ? String((node.args[0] as any).value || '').trim().toLowerCase()
            : null
          const nSql = arg1
          const dSql = coerceDateAddOperand(arg2, node.args?.[2])
          const mul = (k: number) => (k === 1 ? `(${nSql})` : `((${nSql}) * ${k})`)
          if (!partRaw) {
            outErrs.push({ message: 'DATEADD requires a literal date_part (e.g. "day")', start: node.span.start, end: node.span.end })
            return `(${dSql} + (${nSql}) * INTERVAL 1 DAY)`
          }
          const part = partRaw.replace(/'/g, '').trim()
          if (part === 'year' || part === 'yy' || part === 'yyyy') return `(${dSql} + ${mul(1)} * INTERVAL 1 YEAR)`
          if (part === 'quarter' || part === 'qq') return `(${dSql} + ${mul(3)} * INTERVAL 1 MONTH)`
          if (part === 'month' || part === 'mm') return `(${dSql} + ${mul(1)} * INTERVAL 1 MONTH)`
          if (part === 'week' || part === 'wk') return `(${dSql} + ${mul(7)} * INTERVAL 1 DAY)`
          if (part === 'day' || part === 'dd') return `(${dSql} + ${mul(1)} * INTERVAL 1 DAY)`
          if (part === 'hour' || part === 'hh') return `(${dSql} + ${mul(1)} * INTERVAL 1 HOUR)`
          if (part === 'minute' || part === 'mi') return `(${dSql} + ${mul(1)} * INTERVAL 1 MINUTE)`
          if (part === 'second' || part === 'ss') return `(${dSql} + ${mul(1)} * INTERVAL 1 SECOND)`
          outErrs.push({ message: `Unsupported DATEADD part "${partRaw}"`, start: node.span.start, end: node.span.end })
          return `(${dSql} + (${nSql}) * INTERVAL 1 DAY)`
        }
        if (callName === 'datediff') {
          if ((node.args?.length ?? 0) < 3) {
            outErrs.push({ message: 'DATEDIFF(date_part, start, end) requires 3 arguments', start: node.span.start, end: node.span.end })
            return 'NULL'
          }
          const partRaw = (node.args?.[0]?.kind === 'literal' && (node.args[0] as any).literal_kind === 'string')
            ? String((node.args[0] as any).value || '').trim().toLowerCase()
            : null
          const startSql = `TRY_CAST(${coerceDateAddOperand(arg1, node.args?.[1])} AS TIMESTAMP)`
          const endSql = `TRY_CAST(${coerceDateAddOperand(arg2, node.args?.[2])} AS TIMESTAMP)`
          if (!partRaw) {
            outErrs.push({ message: 'DATEDIFF requires a literal date_part (e.g. "day")', start: node.span.start, end: node.span.end })
            return `DATE_DIFF('day', ${startSql}, ${endSql})`
          }
          const part = partRaw.replace(/'/g, '').trim()
          if (part === 'quarter' || part === 'qq') return `(DATE_DIFF('month', ${startSql}, ${endSql}) / 3)`
          const duckPart =
            (part === 'year' || part === 'yy' || part === 'yyyy') ? 'year'
            : (part === 'month' || part === 'mm') ? 'month'
            : (part === 'week' || part === 'wk') ? 'week'
            : (part === 'day' || part === 'dd') ? 'day'
            : (part === 'hour' || part === 'hh') ? 'hour'
            : (part === 'minute' || part === 'mi') ? 'minute'
            : (part === 'second' || part === 'ss') ? 'second'
            : null
          if (!duckPart) {
            outErrs.push({ message: `Unsupported DATEDIFF part "${partRaw}"`, start: node.span.start, end: node.span.end })
            return `DATE_DIFF('day', ${startSql}, ${endSql})`
          }
          return `DATE_DIFF('${duckPart}', ${startSql}, ${endSql})`
        }
        if (callName === 'datepart') {
          if ((node.args?.length ?? 0) < 2) {
            outErrs.push({ message: 'DATEPART(date_part, date) requires 2 arguments', start: node.span.start, end: node.span.end })
            return 'NULL'
          }
          const partRaw = (node.args?.[0]?.kind === 'literal' && (node.args[0] as any).literal_kind === 'string')
            ? String((node.args[0] as any).value || '').trim().toLowerCase()
            : null
          const dSql = arg1
          if (!partRaw) {
            outErrs.push({ message: 'DATEPART requires a literal date_part (e.g. "year")', start: node.span.start, end: node.span.end })
            return `DATE_PART('year', ${dSql})`
          }
          const part = partRaw.replace(/'/g, '').trim()
          if (part === 'weekday' || part === 'dayofweek' || part === 'dw') {
            return `(CAST(DATE_PART('dow', ${dSql}) AS INTEGER) + 1)`
          }
          if (part === 'quarter' || part === 'qq') return `DATE_PART('quarter', ${dSql})`
          const duckPart =
            (part === 'year' || part === 'yy' || part === 'yyyy') ? 'year'
            : (part === 'month' || part === 'mm') ? 'month'
            : (part === 'week' || part === 'wk') ? 'week'
            : (part === 'day' || part === 'dd') ? 'day'
            : (part === 'hour' || part === 'hh') ? 'hour'
            : (part === 'minute' || part === 'mi') ? 'minute'
            : (part === 'second' || part === 'ss') ? 'second'
            : null
          if (!duckPart) {
            outErrs.push({ message: `Unsupported DATEPART part "${partRaw}"`, start: node.span.start, end: node.span.end })
            return `DATE_PART('year', ${dSql})`
          }
          return `DATE_PART('${duckPart}', ${dSql})`
        }
        if (callName === 'datename') {
          if ((node.args?.length ?? 0) < 2) {
            outErrs.push({ message: 'DATENAME(date_part, date) requires 2 arguments', start: node.span.start, end: node.span.end })
            return 'NULL'
          }
          const partRaw = (node.args?.[0]?.kind === 'literal' && (node.args[0] as any).literal_kind === 'string')
            ? String((node.args[0] as any).value || '').trim().toLowerCase()
            : null
          const dSql = arg1
          if (!partRaw) {
            outErrs.push({ message: 'DATENAME requires a literal date_part (e.g. "month")', start: node.span.start, end: node.span.end })
            return `CAST(DATE_PART('year', ${dSql}) AS VARCHAR)`
          }
          const part = partRaw.replace(/'/g, '').trim()
          if (part === 'month' || part === 'mm') return `STRFTIME(${dSql}, '%B')`
          if (part === 'weekday' || part === 'dayofweek' || part === 'dw') return `STRFTIME(${dSql}, '%A')`
          if (part === 'quarter' || part === 'qq') return `CAST(DATE_PART('quarter', ${dSql}) AS VARCHAR)`
          const duckPart =
            (part === 'year' || part === 'yy' || part === 'yyyy') ? 'year'
            : (part === 'day' || part === 'dd') ? 'day'
            : (part === 'hour' || part === 'hh') ? 'hour'
            : (part === 'minute' || part === 'mi') ? 'minute'
            : (part === 'second' || part === 'ss') ? 'second'
            : null
          if (!duckPart) {
            outErrs.push({ message: `Unsupported DATENAME part "${partRaw}"`, start: node.span.start, end: node.span.end })
            return `CAST(DATE_PART('year', ${dSql}) AS VARCHAR)`
          }
          return `CAST(DATE_PART('${duckPart}', ${dSql}) AS VARCHAR)`
        }

        // Core LOD behavior: aggregates become windowed aggregates over the LOD partition dims.
        // If aggregating a nested LOD, preserve the dropped outer aggregate as a comment tag so
        // grouped-query LOD rewrites can restore the correct semantics later.
        if (callName === 'countd' && node.args?.[0]?.kind === 'lod') {
          return `/* LOD_OUTER_AGG COUNTD */ ${arg0}`
        }
        if ((callName === 'count' || callName === 'sum' || callName === 'avg' || callName === 'min' || callName === 'max') && node.args?.[0]?.kind === 'lod') {
          return `/* LOD_OUTER_AGG ${callName.toUpperCase()} */ ${arg0}`
        }
        if (callName === 'agg') {
          return `${arg0}`
        }
        if (callName === 'attr') {
          const v = `CAST(${arg0} AS VARCHAR)`
          const cnt = `COUNT(${v}) OVER (${overClause})`
          const mn = `MIN(${v}) OVER (${overClause})`
          const mx = `MAX(${v}) OVER (${overClause})`
          return `CASE WHEN ${cnt} = 0 THEN NULL WHEN ${mn} = ${mx} THEN ${mn} ELSE '*' END`
        }
        if (callName === 'countd') {
          return `COUNT(DISTINCT ${arg0}) OVER (${overClause})`
        }
        if (callName === 'sum' || callName === 'avg') {
          return `${callName.toUpperCase()}(${numericAggArg(arg0)}) OVER (${overClause})`
        }
        if (callName === 'count' || callName === 'min' || callName === 'max') {
          return `${callName.toUpperCase()}(${arg0}) OVER (${overClause})`
        }

        // Fallback: emit as a DuckDB function call; aggregates inside args were already rewritten above.
        return `${String(node.name).toUpperCase()}(${compiledArgs.join(', ')})`
      }
      return 'NULL'
    }

    const sql = compileNode(n)
    return { sql, deps: outDeps, errs: outErrs }
  }

  const expr = compileLodValueExpr(ast.expression)
  deps.push(...expr.deps)
  errs.push(...expr.errs)

  const lodType = ast.lod_type.toUpperCase()
  return { sql: `/* LOD ${lodType} */ ${expr.sql}`, deps, errs }
}

/**
 * Extract viz-level dimensions from the visual spec.
 * 
 * Returns dimension expressions from rows and columns shelves that are
 * used for grouping in the visualization.
 */
function extractVizDimensions(spec: VisualSpec): CalcAst[] {
  const dims: CalcAst[] = []
  
  // Extract from rows shelf
  const rows = spec.shelves?.rows || []
  for (const row of rows) {
    if (row.field && !row.aggregation) {
      // Dimension field (not aggregated)
      dims.push({
        kind: 'field_ref',
        source: row.source_id || null,
        field: row.field,
        span: { start: 0, end: 0 },
      })
    }
  }
  
  // Extract from columns shelf
  const cols = spec.shelves?.columns || []
  for (const col of cols) {
    if (col.field && !col.aggregation) {
      // Dimension field (not aggregated)
      dims.push({
        kind: 'field_ref',
        source: col.source_id || null,
        field: col.field,
        span: { start: 0, end: 0 },
      })
    }
  }
  
  return dims
}

export function compileCalculatedFieldInSpec(spec: VisualSpec, calc: CalculatedField): CalculatedField {
  const lang = calc.language
  const scope: CalcScope = calc.scope ?? (calc.source_id ? { kind: 'source', source_id: calc.source_id } : { kind: 'global' })
  if (lang !== 'tableau') {
    return { ...calc, duckdb_sql: undefined }
  }
  const expression = String(calc.expression || calc.formula || '').trim()
  const res = compileTableauExpressionToDuckdbSql({ spec, scope, expression })

  // If this calc is a table calc, inject OVER(...) based on stored compute-using config.
  let duckdbSql = res.duckdb_sql
  let nextTableCalc: TableCalcConfig | undefined = calc.table_calc ?? res.table_calc
  if (nextTableCalc?.enabled && (duckdbSql.includes('__TBLCALC__') || duckdbSql.includes('__TBLCALC_PARTITION__'))) {
    // Full OVER clause replacement (PARTITION BY + ORDER BY + optional frame)
    if (duckdbSql.includes('__TBLCALC__')) {
      // If the compiled SQL already embeds a ROWS/RANGE frame after the placeholder,
      // do not also append a frame from compute-using config (avoid double "ROWS BETWEEN ...").
      const hasInlineFrame = /__TBLCALC__\s+(ROWS|RANGE)\b/i.test(duckdbSql)
      const cfg = hasInlineFrame ? { ...nextTableCalc.compute_using, frame: null } : nextTableCalc.compute_using
      const over = renderOverClause(cfg)
      if (over.errors.length) {
        // Surface errors as compilation errors (best-effort)
        // (we attach by appending a SQL comment to avoid breaking query gen)
        duckdbSql = `${duckdbSql} /* tablecalc: ${over.errors.join(' | ')} */`
      }
      const clause = over.over || ''
      duckdbSql = rewriteLinearPreviousValueSequence(duckdbSql, clause)
      duckdbSql = duckdbSql.replaceAll('__TBLCALC__', clause)
    }

    // Partition-only replacement (used by table calcs that embed their own ORDER BY expression)
    if (duckdbSql.includes('__TBLCALC_PARTITION__')) {
      const cfgPart = { ...nextTableCalc.compute_using, addressing: [], order_by: [], frame: null }
      const overPart = renderOverClause(cfgPart)
      if (overPart.errors.length) {
        duckdbSql = `${duckdbSql} /* tablecalc-partition: ${overPart.errors.join(' | ')} */`
      }
      const clausePart = overPart.over || ''
      duckdbSql = duckdbSql.replaceAll('__TBLCALC_PARTITION__', clausePart)
    }
  }
  return {
    ...calc,
    expression,
    duckdb_sql: duckdbSql,
    dependencies: res.dependencies,
    result_type: res.result_type as any,
    table_calc: nextTableCalc,
  }
}

/**
 * Compile all calculated fields in a VisualSpec (best-effort).
 *
 * This:
 * - Computes a Tableau-like evaluation order (row -> aggregate -> LOD -> table calc, with dependency sorting for table calcs)
 * - Compiles each calculated field (Tableau language) to DuckDB SQL
 * - Returns a new spec with updated `data_pane.calculated_fields`
 *
 * NOTE: The compiler can inline dependent calcs even without pre-compilation, but we still
 * run this to materialize `duckdb_sql`, `dependencies`, and `result_type` for downstream consumers.
 */
export function compileAllCalculatedFieldsInSpec(spec: VisualSpec): VisualSpec {
  const calcs = Array.isArray(spec?.data_pane?.calculated_fields) ? (spec.data_pane.calculated_fields as CalculatedField[]) : []
  if (calcs.length === 0) return spec

  // De-dupe by calc id (keeping the first occurrence).
  const byId = new Map<string, CalculatedField>()
  for (const c of calcs) {
    const id = String((c as any)?.id ?? '').trim()
    if (!id) continue
    if (!byId.has(id)) byId.set(id, c)
  }
  const unique = Array.from(byId.values())

  const ordered = getCompleteCalcOrder(unique, spec)

  const compiledById = new Map<string, CalculatedField>()
  for (const c of ordered) {
    const id = String((c as any)?.id ?? '').trim()
    if (!id) continue
    compiledById.set(id, compileCalculatedFieldInSpec(spec, c))
  }

  const nextCalcs = calcs.map((c) => {
    const id = String((c as any)?.id ?? '').trim()
    return (id && compiledById.has(id)) ? (compiledById.get(id) as any) : c
  })
  return { ...spec, data_pane: { ...(spec as any).data_pane, calculated_fields: nextCalcs } } as any
}
